import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CoaEventGroup,
  LocationId,
  SpotEventGroup,
  VoyageAttributeById,
  WithDbId,
  WorkingHours,
} from '../components/core/Core';
import {
  GroupByVesselSortByTime,
  groupCoaEntriesToSequences,
  groupSpotEntriesToSequences,
  isOffHireEntry,
  isSingleEntry,
  isTCOrderEntry,
} from '../components/core/Core';
import {
  defaultVessel,
  getVesselBannedChannels,
} from '../components/core/Vessel';
import { DeriveCapacitiesAndTimes } from '../components/core/DeriveCapacitiesAndTimes';
import type { CalculationConfig } from '../components/core/ScheduleDataContext';
import type { CargoRanges } from '../components/core/Capacity';
import type { VesselToSortedEvents } from '../components/core/VesselSchedule';
import type { Vessel } from '../components/core/Vessel';
import type { ScheduleDataContext } from '../components/core/ScheduleDataContext';
import type {
  CargoAllocation,
  CoaEntry,
  OffHireEntry,
  PortCallEntry,
  SingleEntry,
  SpotEntry,
  TCOrderEntry,
} from '@seaber/common/core/entries';
import { EventCategory } from '@seaber/common/core/entries';
import type {
  Events,
  CoaTimeDataGroup,
  SpotTimeDataGroup,
  TcTimeDataGroup,
} from '../components/core/TimeData';
import type {
  EstimateTimeline,
  InternalBackupSchedule,
  InternalMasterSchedule,
  InternalSuggestionSchedule,
  InternalUserSchedule,
  ScenarioOrderWithShipmentId,
  UseScenariosResult,
} from '../components/Scenario';
import {
  CargoAllocationFragmentToEntry,
  coaFragmentToEntry,
  dummyScenario,
  isMasterScenario,
  isSuggestionScenario,
  isUserScenario,
  offHireFragmentToOffHireEntry,
  getScenarioOrdersAndShipments,
  portCallFragmentToEntry,
  SCENARIO_DATA_SCHEMA_VERSION,
  scheduledOrderFragmentToTCOrderEntry,
  singleEventFragmentToSingleEntry,
  spotFragmentToEntry,
  useScenarios,
} from '../components/Scenario';
import {
  updateScenarioFromArchivedState,
  updateScenarioFromTimelines,
  type ArchivedState,
} from '../components/ScenarioUtil';
import type {
  MasterSchedule,
  ScenarioSchedule,
} from '@seaber/common/core/scenario';
import type { InternalInitialSchedule } from '@seaber/common/core/scenario';
import type {
  AllOrdersQuery,
  BatchDataQuery,
  CommitInput,
  CommitLogUpdatedSubscription,
  CommitPayload,
  CommitsSinceDisconnectQuery,
  CommitsSinceDisconnectQueryVariables,
  CreatedOrExisting,
  Ident,
  LastCommitFragment,
  OrderPatch,
  PlanningPageQuery,
  ReconnectQuery,
  ScenariosByIdsQuery,
  ScenariosByIdsQueryVariables,
  ScenarioTimeline_CargoCompartmentAllocationsFragment,
  ScenarioTimeline_CoaAssignmentFragment,
  ScenarioTimeline_CoaFragment,
  ScenarioTimeline_HandledBySpotFragment,
  ScenarioTimeline_OffHireFragment,
  ScenarioTimeline_OrderFragment,
  ScenarioTimeline_OrderLocationFragment,
  ScenarioTimeline_OrganizationFragment,
  ScenarioTimeline_PortCall_FilesFragment,
  ScenarioTimeline_PortCallFragment,
  ScenarioTimeline_ShipFragment,
  ScenarioTimeline_ShipmentFragment,
  ScenarioTimeline_SingleEventFragment,
  ScenarioTimeline_VoyageCharterToContractFragment,
  ScheduledOrderFragment,
  Shipment,
  ShipmentFreightId,
  ShipmentExtraFreightId,
  ShipmentId,
  UserWithNameFragment,
  VesselAttribute,
  VesselSpeedConsumptionFragment,
  VesselDraftDatapointFragment,
  AllVesselAttributesQuery,
  AllVoyageAttributesQuery,
  Costs_FreightFragment,
  VoyageIncome_VoyageCharterOrderFreightFragment,
  PaymentUnit,
  ScheduledOrderId,
  HandledByCoaId,
  HandledBySpotId,
  SingleEventPortCallRelationId,
  SingleEventId,
  PortCallId,
  OffHireId,
  CargoCompartmentAllocationId,
  CargoCompartmentId,
  OrderId,
  OrderPortCallRelationId,
  OrderVoyageCharterRelationId,
  VoyageCharterOrderFreightId,
  CoaContractPortId,
  VoyageCharterId,
  VoyageCharterToContractId,
  CoaContractId,
  BatchUpdateSubscriptionVariables,
  BatchUpdateSubscription,
} from '../graphql';
import {
  AllOrdersDocument,
  BatchDataDocument,
  CommitsSinceDisconnectDocument,
  EventType,
  OffHireType,
  PlanningPageDocument,
  ReconnectDocument,
  ScenariosByIdsDocument,
  useAllVesselAttributesQuery,
  useAllVoyageAttributesQuery,
  useBatchUpdateSubscription,
  useCommitLogUpdatedSubscription,
  useDbChangeSubscription,
  usePlanningPageQuery,
  useUsersQuery,
} from '../graphql';
import type { Range } from './util';
import {
  assertDefined,
  assertedDefined,
  exhaustiveCheck,
  isDefined,
  mapKeyBy,
  mapToSet,
  memoize,
  memoize2,
  memoize3,
  memoize4,
  splitM,
} from './util';
import type { EventTimeline } from '../components/core/Timelines';
import { getTimelines } from '../components/core/Timelines';
import { usePlanningUpdates } from '../components/hooks/UsePlanningUpdates';
import type { ClientId } from '../components/core/ClientId';
import { parseClientId } from '../components/core/ClientId';
import { toast } from 'react-toastify';
import {
  createIntegrationOrdersToast,
  mapToOrderSummary,
} from '../components/notifications/IntegrationOrdersToast';
import { dateStringToNumOpt } from '../components/core/util';
import { useDistances } from '../components/distances/hooks';
import { getResolvedLocationPairsFromEvents } from '../components/distances/distance-utils';
import type * as ApolloReactHooks from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';

import type { PortCostEstimateMaps } from '../components/hooks/usePortCostEstimates';
import { usePortCostEstimates } from '../components/hooks/usePortCostEstimates';
import type { User } from '../state/zustand';
import {
  hasFeature,
  useBunkerEx,
  useCurrencyRates,
  useCurrentUser,
  useDefaultCurrency,
} from '../state/zustand';
import { useAssortmentExclusions } from '../components/hooks/useAssortmentExclusions';
import { onReconnect } from './init-apollo';
import useEvent from '../components/hooks/useEvent';
import { useAllLocations } from './allLocations';
import type { Distances, LocationPair } from '../components/distances/context';
import moment from 'moment';
import type { ScenarioShipment } from '@seaber/common/core/scenario';
import { getToken } from './cookieParsers';
import type { BinaryPlanningPageQuery } from '../../cpp-backend/initialData';
import { prepareInitialPlanningPageData } from '../../cpp-backend/initialData';
import { binaryPlanningPageQuerySchema } from '../../cpp-backend/binaryValidator';
import {
  DefaultMap,
  mapGroupBy2,
  mapGroupBy3,
  mapKeyBy2,
  mapKeyBy2M,
  mapKeyByM,
} from '@seaber/common/lib/collections';
import { parse } from './parsers/parsers';
import { useCarbonPrice } from '../components/hooks/useCarbonPrice';
import { transformAreaBunkerGradeMappings } from './org-area-bunker-grade.utils';
import {
  parseBunkerEx,
  useBunkerPrices,
} from '../components/hooks/useBunkerPrices';
import { transformPortCommodityLdRates } from './port-commodity-ld-rates.utils';
import { portCostPrioritySchema } from '../components/shipment/costs/costs';
import { getMarcuraPortCostPairs } from '../components/marcura/util';
import { useMarcuraPortCosts } from '../components/marcura/hooks';
import { parseBunkerGradeCalculations } from './bunker-grade-mappings.utils';
import type { BatchData } from '../../common/core/batch';
import type { EmptyObject } from 'react-hook-form';

export type { ScenarioShipment };

export type ScenarioLocation = Omit<
  ScenarioTimeline_OrderLocationFragment,
  'workingHours'
> & { workingHours?: WorkingHours | null };

/** Derived order type after combining actual orders and scenario data. Not stored to scenario in its entirety, only as partial patches. */
export type ScenarioOrder = Omit<
  ScenarioTimeline_OrderFragment,
  | 'shipmentId'
  | 'cargoCompartmentAllocationsByOrderId'
  | 'locationByDischargePortId'
  | 'locationByLoadPortId'
> & {
  shipmentId: ClientId | null;
  shipment: ScenarioShipment | null;
  /** Should only be read when constructing TCOrderEvents. During assignment creation may be outdated. */
  cargoAllocations: CargoAllocation[];

  /** L/D windows are accessed a lot, pre-parsed from laycan & rta/deliveredBefore for better performance */
  loadingWindow: Range<number | undefined>;
  dischargingWindow: Range<number | undefined>;

  /** Order sizes are accessed a lot, pre-compute from cargoAmount & unit values for better performance */
  cargoRanges: CargoRanges;

  locationByDischargePortId?: ScenarioLocation | null;
  locationByLoadPortId?: ScenarioLocation | null;
};

export type PersistedShipment = ScenarioShipment & {
  id: number;
};

const shipmentFragmentToPersistedShipment = memoize(
  (
    s: Pick<
      Shipment,
      | 'id'
      | 'clientId'
      | 'name'
      | 'remarks'
      | 'externalShipmentId'
      | 'shipmentExternalStatusesByShipmentId'
      | 'voyageAttributeId'
      | 'shipmentFreightByShipmentId'
      | 'shipmentExtraFreightsByShipmentId'
    >,
  ): PersistedShipment => ({
    id: s.id,
    clientId: parseClientId(s.clientId),
    name: s.name,
    remarks: s.remarks ?? null,
    externalShipmentId: s.externalShipmentId ?? null,
    externalShipmentStatus:
      s.shipmentExternalStatusesByShipmentId?.nodes?.at(0)?.status ?? null,
    voyageAttributeId: s.voyageAttributeId ?? null,
    shipmentFreightByShipmentId: s.shipmentFreightByShipmentId ?? null,
    shipmentExtraFreightsByShipmentId: compact(
      s.shipmentExtraFreightsByShipmentId?.nodes,
    ),
  }),
);

export type Ships = ScenarioTimeline_ShipFragment;

export type MasterScenarioData = ComputedScenarioTimings['masterData'];
export type UserScenarioData = ComputedScenarioTimings['selectedScenarioData'];
export type TcTimeDataByVessel = Map<Ships['id'], TcTimeDataGroup>;

export const parseVesselAttributes = (
  allVesselAttributesResult: AllVesselAttributesQuery | undefined,
): Map<number, Pick<VesselAttribute, 'id' | 'name'>> => {
  const vesselAttributes = compact(
    allVesselAttributesResult?.allVesselAttributes?.nodes,
  );
  return mapKeyBy(vesselAttributes, getId);
};

function useVesselAttributes(
  skip = false,
): Map<number, Pick<VesselAttribute, 'id' | 'name'>> {
  const allVesselAttributesResult = useAllVesselAttributesQuery({ skip });
  return useMemo(
    () => parseVesselAttributes(allVesselAttributesResult.data),
    [allVesselAttributesResult],
  );
}

export const parseVoyageAttributes = (
  allVoyageAttributesResult: AllVoyageAttributesQuery | undefined,
): VoyageAttributeById => {
  const voyageAttributes = compact(
    allVoyageAttributesResult?.allVoyageAttributes?.nodes,
  );
  return mapKeyBy(voyageAttributes, getId);
};

function useVoyageAttributes(skip = false): VoyageAttributeById {
  const allVoyageAttributesResult = useAllVoyageAttributesQuery({ skip });
  return useMemo(
    () => parseVoyageAttributes(allVoyageAttributesResult.data),
    [allVoyageAttributesResult],
  );
}

type LastCommitNode = NonNullable<
  NonNullable<PlanningPageQuery['lastCommit']>['nodes'][0]
>;
type ShipNode = NonNullable<
  NonNullable<PlanningPageQuery['allShips']>['nodes'][0]
>;
type TcContractNode = NonNullable<
  NonNullable<ShipNode['tcContractsByVesselId']['nodes'][number]>
>;
type VesselInfoNode = NonNullable<
  NonNullable<ShipNode['vesselInfoByVesselInfoId']>
>;
type VesselFulfillsRequirementAssociationNode = NonNullable<
  NonNullable<
    ShipNode['vesselFulfillsRequirementAssociationsByVesselId']
  >['nodes'][0]
>;
type OrderNode = NonNullable<
  NonNullable<PlanningPageQuery['allOrders']>['nodes'][0]
>;
type ScheduledOrderNode = NonNullable<
  NonNullable<PlanningPageQuery['allScheduledOrders']>['nodes'][0]
>;
type HandledBySpotNode = NonNullable<
  NonNullable<PlanningPageQuery['allHandledBySpots']>['nodes'][0]
>;
type SingleEventNode = NonNullable<
  NonNullable<PlanningPageQuery['allSingleEvents']>['nodes'][0]
>;
type OffHireNode = NonNullable<
  NonNullable<PlanningPageQuery['allOffHires']>['nodes'][0]
>;
type CoaContractNode = NonNullable<
  NonNullable<PlanningPageQuery['allCoaContracts']>['nodes'][0]
>;
type HandledByCoaNode = NonNullable<
  NonNullable<PlanningPageQuery['allHandledByCoas']>['nodes'][0]
>;
type OrganizationNode = NonNullable<
  NonNullable<PlanningPageQuery['allOrganizations']>['nodes'][0]
>;
type PortCallNode = NonNullable<
  NonNullable<PlanningPageQuery['allPortCalls']>['nodes'][0]
>;
type ShipmentNode = NonNullable<
  NonNullable<PlanningPageQuery['allShipments']>['nodes'][0]
>;

type ZoneNode = NonNullable<
  NonNullable<PlanningPageQuery['allZones']>['nodes'][0]
>;

type ZoneLocationNode = NonNullable<
  ZoneNode['zoneLocationsByZoneId']['nodes'][0]
>;
type AssortmentNode = NonNullable<OrderNode['assortmentByAssortmentId']>;
type AssortmentGroupNode = NonNullable<
  AssortmentNode['assortmentGroupByAssortmentGroup']
>;
type CoaContractAssortmentNode = NonNullable<
  CoaContractNode['coaAssortments']['nodes'][0]
>;
type CoaContractShipNode = NonNullable<CoaContractNode['vessels']['nodes'][0]>;
type CoaContractPortDatumNode = NonNullable<
  CoaContractNode['portDataRelations']['nodes'][0]
>;
type PortDatumNode = NonNullable<CoaContractPortDatumNode['portData']>;
type OrderPortCallRelationNode = NonNullable<
  OrderNode['orderPortCallRelationsByOrderId']['nodes'][0]
>;
type SingleEventPortCallRelationNode = NonNullable<
  SingleEventNode['singleEventPortCallRelationsBySingleEventId']['nodes'][0]
>;
export type OrderVoyageCharterRelationNode = NonNullable<
  OrderNode['incomingVoyageCharterRelation']
> &
  NonNullable<OrderNode['outgoingVoyageCharterRelation']>;
type VoyageCharterNode = NonNullable<
  OrderVoyageCharterRelationNode['voyageCharterByVoyageCharterId']
>;
type VoyageCharterToContractNode = NonNullable<
  VoyageCharterNode['voyageCharterToContractsByVoyageCharterId']['nodes'][0]
>;
type VoyageCharterCoaContractNode = NonNullable<
  VoyageCharterToContractNode['coaContractByCoaId']
>;
type CoaContractPriceNode = NonNullable<
  CoaContractNode['coaContractPricesByCoaContractId']['nodes'][0]
> &
  NonNullable<
    VoyageCharterCoaContractNode['coaContractPricesByCoaContractId']['nodes'][0]
  >;
type CoaContractPortNode = NonNullable<CoaContractNode['ports']['nodes'][0]> &
  NonNullable<
    VoyageCharterCoaContractNode['coaContractPortsByCoaContractId']
  >['nodes'][0];
type VoyageCharterExtraCostNode = NonNullable<
  NonNullable<
    VoyageCharterNode['voyageCharterExtraCostsByVoyageCharterId']
  >['nodes'][0]
>;
type VoyageCharterExtraFreightNode = NonNullable<
  NonNullable<
    VoyageCharterNode['voyageCharterExtraFreightsByVoyageCharterId']
  >['nodes'][0]
>;
type VoyageCharterCoaContractAssortmentNode = NonNullable<
  NonNullable<
    VoyageCharterCoaContractNode['coaContractAssortmentsByCoaContractId']
  >['nodes'][0]
>;
type CoaContractBunkerNode = NonNullable<
  VoyageCharterCoaContractNode['coaContractBunkersByCoaContractId']['nodes'][0]
>;
type CommissionNode = NonNullable<
  VoyageCharterNode['commissionsByVoyageCharterId']['nodes'][0]
>;
type VoyageCharterOrderFreightNode = NonNullable<
  NonNullable<
    OrderVoyageCharterRelationNode['voyageCharterOrderFreightsByOrderVoyageCharterRelationId']
  >['nodes'][0]
>;
type ApiOrdersNoticeNode = NonNullable<
  NonNullable<OrderNode['apiOrdersNoticesByOrderId']>['nodes'][0]
>;
type VoyageCharterPortDatumNode = NonNullable<
  NonNullable<
    VoyageCharterNode['voyageCharterPortDataByVoyageCharterId']
  >['nodes'][0]
>;
type ShipVoyageNamingRuleRelationNode = NonNullable<
  NonNullable<ShipNode['shipVoyageNamingRuleRelationsByShipId']>['nodes'][0]
>;
type OrderDetailNode = NonNullable<
  NonNullable<OrderNode['orderDetailsByOrderId']>['nodes'][0]
>;
type PortNoticeNode = NonNullable<
  NonNullable<PortCallNode['portNoticesByPortCallId']>['nodes'][0]
>;
type OrderAttributeAssociationNode = NonNullable<
  NonNullable<OrderNode['orderAttributeAssociationsByOrderId']>['nodes'][0]
>;
type OrderRequirementAssociationNode = NonNullable<
  NonNullable<OrderNode['orderRequirementAssociationsByOrderId']>['nodes'][0]
>;
type VesselAttributesAssociationNode = NonNullable<
  NonNullable<ShipNode['vesselAttributesAssociationsByShipId']>['nodes'][0]
>;
type VesselAttributeNode = NonNullable<
  VesselAttributesAssociationNode['vesselAttributeByVesselAttributeId']
>;
type CargoCompartmentAllocationNode =
  NonNullable<ScenarioTimeline_CargoCompartmentAllocationsFragment>;
type CargoCompartmentNode = NonNullable<
  ShipNode['cargoCompartmentsByShipId']['nodes'][0]
> &
  NonNullable<VesselInfoNode['cargoCompartmentsByVesselId']['nodes'][0]>;
type CargoUnitNode = NonNullable<OrderNode['cargoUnitByCargoUnitId']>;
type LocationNode = NonNullable<OrderNode['locationByLoadPortId']>;
type CountryNode = NonNullable<LocationNode['countryByCountryId']>;
type VesselSpeedConsumptionNode = NonNullable<
  NonNullable<ShipNode['vesselSpeedConsumptionsByShipId']>['nodes'][0]
>;
type VesselDraftDatapointNode = NonNullable<
  NonNullable<ShipNode['vesselDraftDatapointsByShipId']>['nodes'][0]
>;
type BunkerGradeOverrideNode = NonNullable<
  VesselSpeedConsumptionNode['bunkerGradeOverrideByBunkerGradeId']
>;
type StatementOfFactNode = NonNullable<
  PortCallNode['statementOfFactsByPortCallId']['nodes'][0]
>;
type UserNode = NonNullable<OrderNode['userByUserId']>;
type VoyageNamingRuleNode = NonNullable<
  NonNullable<
    ShipNode['shipVoyageNamingRuleRelationsByShipId']['nodes'][0]
  >['voyageNamingRuleByRuleId']
>;
type PortActivityNode = NonNullable<
  StatementOfFactNode['portActivitiesByStatementOfFactsId']['nodes'][0]
>;
type ShipmentExternalStatusNode = NonNullable<
  ShipmentNode['shipmentExternalStatusesByShipmentId']['nodes'][0]
>;
type PortCallFileNode = NonNullable<
  PortCallNode['portCallFilesByPortCallId']['nodes'][0]
>;
type FileNode = NonNullable<PortCallFileNode['fileByFileId']>;

type CommitLog = NonNullable<
  NonNullable<CommitLogUpdatedSubscription['commitLogUpdated']>['commitLog']
>;
type CommitLogScenario = NonNullable<
  NonNullable<CommitLog['commitLogScenarioByMasterClientId']>
>;

export type DefinedAssortment = Required<AssortmentNode>;
export type DefinedAssortmentGroup = Required<AssortmentGroupNode>;
export type DefinedApiOrdersNotice = Required<ApiOrdersNoticeNode>;
export type DefinedBunkerGradeOverride = Required<BunkerGradeOverrideNode>;
export type DefinedCoaContract = Required<CoaContractNode> &
  Required<VoyageCharterCoaContractNode>;
export type DefinedCoaContractAssortment = Required<CoaContractAssortmentNode> &
  Required<VoyageCharterCoaContractAssortmentNode>;
export type DefinedCoaContractBunker = Required<CoaContractBunkerNode>;
export type DefinedCoaContractPort = Required<CoaContractPortNode>;
export type DefinedCoaContractPortDatum = Required<CoaContractPortDatumNode>;
export type DefinedCoaContractShip = Required<CoaContractShipNode>;
export type DefinedCoaContractPrice = Required<CoaContractPriceNode>;
export type DefinedCommitLogScenario = Required<CommitLogScenario>;
export type DefinedLocation = Required<LocationNode>;
export type DefinedCountry = Required<CountryNode>;
export type DefinedCargoUnit = Required<CargoUnitNode>;
export type DefinedCargoCompartmentAllocation =
  Required<CargoCompartmentAllocationNode>;
export type DefinedCargoCompartment = Required<CargoCompartmentNode>;
export type DefinedFile = Required<FileNode>;
export type DefinedHandledByCoa = Required<HandledByCoaNode>;
export type DefinedHandledBySpot = Required<HandledBySpotNode>;
export type DefinedCommitLog = Required<LastCommitNode>;
export type DefinedOrder = Required<OrderNode>;
export type DefinedOrderAttributeAssociation =
  Required<OrderAttributeAssociationNode>;
export type DefinedOrderDetail = Required<OrderDetailNode>;
export type DefinedOffHire = Required<OffHireNode>;
export type DefinedOrderPortCallRelation = Required<OrderPortCallRelationNode>;
export type DefinedOrderRequirementAssociation =
  Required<OrderRequirementAssociationNode>;
export type DefinedOrderVoyageCharterRelation =
  Required<OrderVoyageCharterRelationNode>;
export type DefinedOrganization = Required<OrganizationNode>;
export type DefinedCommission = Required<CommissionNode>;
export type DefinedPortActivity = Required<PortActivityNode>;
export type DefinedPortCall = Required<PortCallNode>;
export type DefinedPortCallFile = Required<PortCallFileNode>;
export type DefinedPortDatum = Required<PortDatumNode>;
export type DefinedPortNotice = Required<PortNoticeNode>;
export type DefinedShipment = Required<ShipmentNode>;
export type DefinedSingleEvent = Required<SingleEventNode>;
export type DefinedSingleEventPortCallRelation =
  Required<SingleEventPortCallRelationNode>;
export type DefinedShipmentExternalStatus =
  Required<ShipmentExternalStatusNode>;
export type DefinedScheduledOrder = Required<ScheduledOrderNode>;
export type DefinedStatementOfFact = Required<StatementOfFactNode>;
export type DefinedShipVoyageNamingRuleRelation =
  Required<ShipVoyageNamingRuleRelationNode>;
export type DefinedShip = Required<ShipNode>;
export type DefinedTcContract = Required<TcContractNode>;
export type DefinedUser = Required<UserNode>;
export type DefinedVesselAttributesAssociation =
  Required<VesselAttributesAssociationNode>;
export type DefinedVesselAttribute = Required<VesselAttributeNode>;
export type DefinedVoyageCharterOrderFreight =
  Required<VoyageCharterOrderFreightNode>;
export type DefinedVoyageCharterExtraCost =
  Required<VoyageCharterExtraCostNode>;
export type DefinedVoyageCharterExtraFreight =
  Required<VoyageCharterExtraFreightNode>;
export type DefinedVoyageCharterPortDatum =
  Required<VoyageCharterPortDatumNode>;
export type DefinedVoyageCharter = Required<VoyageCharterNode>;
export type DefinedVoyageCharterToContract =
  Required<VoyageCharterToContractNode>;
export type DefinedVesselFulfillsRequirementAssociation =
  Required<VesselFulfillsRequirementAssociationNode>;
export type DefinedVesselInfo = Required<VesselInfoNode>;
export type DefinedVoyageNamingRule = Required<VoyageNamingRuleNode>;
export type DefinedVesselSpeedConsumption =
  Required<VesselSpeedConsumptionNode>;
export type DefinedVesselDraftDatapoint = Required<VesselDraftDatapointNode>;
export type DefinedZone = Required<ZoneNode>;
export type DefinedZoneLocation = Required<ZoneLocationNode>;

export type DefinedPlanningPageQuery = {
  __typename: 'Query';
  lastCommit: {
    __typename: 'CommitLogsConnection';
    nodes: Array<DefinedCommitLog>;
  };
  allShips: {
    __typename: 'ShipsConnection';
    nodes: Array<DefinedShip>;
  };
  allOrders: {
    __typename: 'OrdersConnection';
    nodes: Array<DefinedOrder>;
  };
  allScheduledOrders: {
    __typename: 'ScheduledOrdersConnection';
    nodes: Array<DefinedScheduledOrder>;
  };
  allHandledBySpots: {
    __typename: 'HandledBySpotsConnection';
    nodes: Array<DefinedHandledBySpot>;
  };
  allSingleEvents: {
    __typename: 'SingleEventsConnection';
    nodes: Array<DefinedSingleEvent>;
  };
  allOffHires: {
    __typename: 'OffHiresConnection';
    nodes: Array<DefinedOffHire>;
  };
  allCoaContracts: {
    __typename: 'CoaContractsConnection';
    nodes: Array<DefinedCoaContract>;
  };
  allHandledByCoas: {
    __typename: 'HandledByCoasConnection';
    nodes: Array<DefinedHandledByCoa>;
  };
  allOrganizations: {
    __typename: 'OrganizationsConnection';
    nodes: Array<DefinedOrganization>;
  };
  allPortCalls: {
    __typename: 'PortCallsConnection';
    nodes: Array<DefinedPortCall>;
  };
  allShipments: {
    __typename: 'ShipmentsConnection';
    nodes: Array<DefinedShipment>;
  };
  allZones: {
    __typename: 'ZonesConnection';
    nodes: Array<DefinedZone>;
  };
  allAssortments: {
    __typename: 'AssortmentsConnection';
    nodes: Array<DefinedAssortment>;
  };
};

type UserWithName = UserWithNameFragment;

type OrderPortCallRelation = NonNullable<
  ScenarioTimeline_OrderFragment['orderPortCallRelationsByOrderId']['nodes'][0]
>;

function applyCommitLogEntry(
  commitLog: CommitLog,
  currentUserId: number | undefined,
  client: ApolloClient<object>,
  locationsById: Map<LocationId, ScenarioLocation>,
  userById: Map<number, UserWithName>,
) {
  if (!commitLog || !commitLog.success || !commitLog.payload) {
    return;
  }
  const {
    id,
    input,
    payload,
    committedBy: userId,
    committedAt,
    commitLogScenarioByMasterClientId,
    masterClientId,
    scenarioClientId,
    userByCommittedBy,
  } = commitLog;
  if (!userId) {
    return;
  }

  const variables = {
    showArchived: false,
  };
  const dataInStore = client.readQuery<PlanningPageQuery>({
    query: PlanningPageDocument,
    variables,
  });
  if (!dataInStore) {
    return;
  }
  const prevCommittedAt = dataInStore.lastCommit?.nodes[0]?.committedAt;
  if (
    prevCommittedAt &&
    Date.parse(committedAt) < Date.parse(prevCommittedAt)
  ) {
    toast.error(
      'Corrupt data received, please refresh the page to ensure all data is up to date',
      { autoClose: false },
    );
    return;
  }

  const inputs: CommitInput = JSON.parse(input);
  const payloads: CommitPayload = JSON.parse(payload);
  const {
    createHandledByCoas,
    createHandledBySpots,
    createPortCalls,
    createShipments,
    createShipmentFreights,
    createShipmentExtraFreights,
    createSingleEventPortCallRelations,

    createCargoCompartmentAllocations,
    createOrderPortCallRelations,
    createOrderVoyageCharterRelations,
    createVoyageCharterToContracts,
    createScheduledOrders,
    createSingleEvents,
    createOffHires,
    createVoyageCharterOrderFreights,

    updateHandledByCoas,
    updateHandledBySpots,
    updateOffHires,
    updateOrders,
    updatePortCalls,
    updateScheduledOrders,
    updateSingleEvents,
    updateCargoCompartmentAllocations,
    updateVoyageCharters,
    updateVoyageCharterToContracts,
    updateVoyageCharterOrderFreights,
    updateShipmentFreights,
    updateShipmentExtraFreights,

    deleteCargoCompartmentAllocations,
    deletePortCalls,
    deleteHandledBySpots,
    deleteHandledByCoas,
    deleteScheduledOrders,
    deleteSingleEvents,
    deleteOffHires,

    deleteOrderVoyageCharterRelations,
    deleteOrderPortCallRelations,
    deleteVoyageCharterToContracts,
    deleteSingleEventPortCallRelations,
    deleteVoyageCharterOrderFreights,
    scenarioCID,
    masterScenarioCommitLogClientId,
    deleteShipmentFreights,
    deleteShipmentExtraFreights,
    /*
    createVoyageCharterPortData, // Not used in planning page query

    deleteVoyageCharterPortData, // Not used in planning page query

    deleteBillOfLadingData, // Not used in planning page query
    deleteCargoMeasurements, // Not used in planning page query
    createVoyageCharters,

    isMerge,
    previousCommitAt,
    scenarioCID,
    suppressEmail,

    createPortNotices, // unused
    updateVoyageCharterPortData, // unused
    deleteVoyageCharters, // unused
    deletePortNotices, // unused
    */
  } = inputs;
  const {
    createdHandledByCoas,
    createdHandledBySpots,
    createdOffHires,
    createdPortCalls,
    createdShipments,
    createdShipmentFreights,
    createdShipmentExtraFreights,
    createdScheduledOrders,
    createdSingleEvents,
    createdSingleEventPortCallRelations,

    createdOrderPortCallRelations,
    createdOrderVoyageCharterRelations,
    createdVoyageCharters,
    createdVoyageCharterToContracts,
    createdCargoCompartmentAllocations,
    createdVoyageCharterOrderFreights,

    updatedShipmentFreights,
    updatedShipmentExtraFreights,

    deletedShipmentFreights,
    deletedShipmentExtraFreights,

    /*
    __typename,
    success,

    createdPortNotices, // unused

    createdVoyageCharterPortData, // Not used in planning page query

    deletedVoyageCharterPortData, // Not used in planning page query

    deletedVoyageCharters, // unused
    deletedPortNotices, // unused

    deletedVoyageCharterOrderFreights,
    deletedBillOfLadingData,
    deletedCargoCompartmentAllocations,
    deletedCargoMeasurements,
    deletedHandledBySpots,
    deletedOrderPortCallRelations,
    deletedOrderVoyageCharterRelations,
    deletedOffHires,
    deletedPortCalls,
    deletedSingleEventPortCallRelations,
    deletedScheduledOrders,
    deletedSingleEvents,
    deletedVoyageCharterToContracts,
    deletedHandledByCoas,

    updatedCargoCompartmentAllocations,
    updatedHandledByCoas,
    updatedHandledBySpots,
    updatedOffHires,
    updatedOrders,
    updatedPortCalls,
    updatedScheduledOrders,
    updatedSingleEvents,
    updatedVoyageCharters,
    updatedVoyageCharterOrderFreights,
    updatedVoyageCharterToContracts,
    updatedShipments,

    updatedVoyageCharterPortData, // unused
     */
  } = payloads;
  const getId = (coe: CreatedOrExisting, ids: Ident[]) => {
    const { created, existing } = coe;
    if (existing != null) {
      return existing;
    } else {
      assertDefined(created, 'id not defined');
      return ids[created].id;
    }
  };
  const createdSOs: DefinedScheduledOrder[] = createdScheduledOrders.map(
    (id, i): DefinedScheduledOrder => {
      const created = createScheduledOrders?.[i];
      assertDefined(created, 'created');
      const userId = created.userId;
      // noinspection UnnecessaryLocalVariableJS
      const so: DefinedScheduledOrder = {
        __typename: 'ScheduledOrder' as const,
        ...created,
        clientId: assertedDefined(
          created.clientId,
          'createScheduledOrders clientId',
        ),
        vesselId: created.vesselId ?? null,
        updatedAt: created.updatedAt ?? null,
        loadingConcurrent: created.loadingConcurrent ?? false,
        loadingStartOverride: created.loadingStartOverride ?? null,
        dischargingConcurrent: created.dischargingConcurrent ?? false,
        dischargingStartOverride: created.dischargingStartOverride ?? null,
        loadingDurationOverride: created.loadingDurationOverride ?? null,
        loadingSeaDurationOverride: created.loadingSeaDurationOverride ?? null,
        dischargingDurationOverride:
          created.dischargingDurationOverride ?? null,
        dischargingSeaDurationOverride:
          created.dischargingSeaDurationOverride ?? null,
        cleaningOmitted: created.cleaningOmitted ?? false,
        userByCreatedByUserId: userById.get(userId) ?? {
          __typename: 'User' as const,
          id: userId,
          name: null,
        },
        id: id.id as ScheduledOrderId,
      };
      return so;
    },
  );
  const createdHBCs = createdHandledByCoas.map((id, i): DefinedHandledByCoa => {
    const created = createHandledByCoas?.[i];
    assertDefined(created, 'created');
    // noinspection UnnecessaryLocalVariableJS
    const hbc: DefinedHandledByCoa = {
      ...created,
      clientId: assertedDefined(
        created.clientId,
        'createHandledByCoas clientId',
      ),
      shared: created.shared ?? false,
      vesselId: created.vesselId ?? null,
      updatedAt: created.updatedAt ?? null,
      metadata: created.metadata ?? null,
      loadingConcurrent: created.loadingConcurrent ?? false,
      loadingStartOverride: created.loadingStartOverride ?? null,
      dischargingConcurrent: created.dischargingConcurrent ?? false,
      dischargingStartOverride: created.dischargingStartOverride ?? null,
      loadingDurationOverride: created.loadingDurationOverride ?? null,
      loadingSeaDurationOverride: created.loadingSeaDurationOverride ?? null,
      dischargingDurationOverride: created.dischargingDurationOverride ?? null,
      dischargingSeaDurationOverride:
        created.dischargingSeaDurationOverride ?? null,
      __typename: 'HandledByCoa' as const,
      id: id.id as HandledByCoaId,
    };
    return hbc;
  });
  const createdHBSs = createdHandledBySpots.map(
    (id, i): DefinedHandledBySpot => {
      const created = createHandledBySpots?.[i];
      assertDefined(created, 'created');
      // noinspection UnnecessaryLocalVariableJS
      const hbs: DefinedHandledBySpot = {
        __typename: 'HandledBySpot' as const,
        ...created,
        orderId: created.orderId ?? null,
        ownerOrganisationId: created.ownerOrganisationId ?? null,
        clientId: assertedDefined(
          created.clientId,
          'createHandledBySpots clientId',
        ),
        shared: created.shared ?? false,
        vesselId: created.vesselId ?? null,
        updatedAt: created.updatedAt ?? null,
        metadata: created.metadata ?? null,
        loadingConcurrent: created.loadingConcurrent ?? false,
        loadingStartOverride: created.loadingStartOverride ?? null,
        dischargingConcurrent: created.dischargingConcurrent ?? false,
        dischargingStartOverride: created.dischargingStartOverride ?? null,
        loadingDurationOverride: created.loadingDurationOverride ?? null,
        loadingSeaDurationOverride: created.loadingSeaDurationOverride ?? null,
        dischargingDurationOverride:
          created.dischargingDurationOverride ?? null,
        dischargingSeaDurationOverride:
          created.dischargingSeaDurationOverride ?? null,
        id: id.id as HandledBySpotId,
      };
      return hbs;
    },
  );
  const createdSEPCRsForSE =
    createSingleEventPortCallRelations?.map(
      (sepcr, i): DefinedSingleEventPortCallRelation => {
        const { singleEventId, portCallId } = sepcr;
        return {
          __typename: 'SingleEventPortCallRelation' as const,
          id: createdSingleEventPortCallRelations[i]
            .id as SingleEventPortCallRelationId,
          singleEventId: getId(
            singleEventId,
            createdSingleEvents,
          ) as SingleEventId,
          portCallId: getId(portCallId, createdPortCalls) as PortCallId,
        };
      },
    ) ?? [];
  const createdRelationById = mapKeyBy(createdSEPCRsForSE, getSingleEventId);
  const createdSE = createdSingleEvents.map((ident, i): DefinedSingleEvent => {
    const created = createSingleEvents?.[i];
    assertDefined(created, 'created');
    const id = ident.id as SingleEventId;
    const rel = createdRelationById.get(id);
    // noinspection UnnecessaryLocalVariableJS
    const se: DefinedSingleEvent = {
      __typename: 'SingleEvent' as const,
      ...created,
      archived: false,
      clientId: assertedDefined(
        created.clientId,
        'createSingleEvents clientId',
      ),
      meta: created.meta ?? null,
      vesselId: created.vesselId ?? null,
      updatedAt: created.updatedAt ?? null,
      concurrent: created.concurrent ?? false,
      startOverride: created.startOverride ?? null,
      seaDurationOverride: created.seaDurationOverride ?? null,
      type: created.type ?? EventType.OTHER,
      id,
      singleEventPortCallRelationsBySingleEventId: {
        __typename: 'SingleEventPortCallRelationsConnection' as const,
        nodes: rel ? [rel] : [],
      },
      locationByLocationId: locationsById.get(created.locationId) ?? null,
      cleaningAssortmentExclusionId:
        created.cleaningAssortmentExclusionId ?? null,
    };
    return se;
  });
  const createdPcs = createdPortCalls.map((ident, i): DefinedPortCall => {
    const created = createPortCalls?.[i];
    assertDefined(created, 'created');
    const id = ident.id as PortCallId;
    // noinspection UnnecessaryLocalVariableJS
    const pc: DefinedPortCall = {
      __typename: 'PortCall' as const,
      ...created,
      seaDurationOverride: created.seaDurationOverride ?? null,
      previousLocationId: created.previousLocationId ?? null,
      clientId: assertedDefined(created.clientId, 'createPortCalls clientId'),
      vesselId: created.vesselId ?? null,
      updatedAt: created.updatedAt ?? null,
      plannedArrival: created.plannedArrival ?? null,
      channelCostDefaultId: created.channelCostDefaultId ?? null,
      legAvoidsChannels: created.legAvoidsChannels ?? false,
      oneTimeOverrideChannelCost: created.oneTimeOverrideChannelCost ?? null,
      oneTimeOverrideCost: created.oneTimeOverrideCost ?? null,
      plannedArrivalOverride: created.plannedArrivalOverride ?? null,
      plannedArrivalOverrideSourcedAt:
        created.plannedArrivalOverrideSourcedAt ?? null,
      plannedArrivalOverrideSourcedFrom:
        created.plannedArrivalOverrideSourcedFrom ?? null,
      plannedBerthing: created.plannedBerthing ?? null,
      plannedBerthingOverride: created.plannedBerthingOverride ?? null,
      plannedBerthingOverrideSourcedAt:
        created.plannedBerthingOverrideSourcedAt ?? null,
      plannedBerthingOverrideSourcedFrom:
        created.plannedBerthingOverrideSourcedFrom ?? null,
      plannedDeparture: created.plannedDeparture ?? null,
      plannedDepartureOverride: created.plannedDepartureOverride ?? null,
      plannedDepartureOverrideSourcedAt:
        created.plannedDepartureOverrideSourcedAt ?? null,
      plannedDepartureOverrideSourcedFrom:
        created.plannedDepartureOverrideSourcedFrom ?? null,
      portCostDefaultId: created.portCostDefaultId ?? null,
      useHistoricalPortCost: created.useHistoricalPortCost ?? false,
      useLatestHistoricalPortCost: created.useLatestHistoricalPortCost ?? false,
      useOneTimeOverrideCost: created.useOneTimeOverrideCost ?? false,
      useIntegrationPortCost: created.useIntegrationPortCost ?? false,
      locationId: created.locationId,
      plannedArrivalDuration: created.plannedArrivalDuration ?? null,
      plannedDepartureDuration: created.plannedDepartureDuration ?? null,
      channelDurationOverhead: created.channelDurationOverhead ?? null,
      plannedBerthingDuration: created.plannedBerthingDuration ?? null,
      totalDischargeDurationOverride:
        created.totalDischargeDurationOverride ?? null,
      totalLoadDurationOverride: created.totalLoadDurationOverride ?? null,
      archived: false,
      id,
      portNoticesByPortCallId: {
        __typename: 'PortNoticesConnection' as const,
        nodes: [],
      },
      statementOfFactsByPortCallId: {
        __typename: 'StatementOfFactsConnection' as const,
        nodes: [],
      },
      portCallFilesByPortCallId: {
        __typename: 'PortCallFilesConnection' as const,
        nodes: [],
      },
      locationByLocationId: locationsById.get(created.locationId) ?? null,
      bunkerOverrides: created.bunkerOverrides ?? null,
      bunkerPriceSource: created.bunkerPriceSource ?? null,
      weatherFactor: created.weatherFactor ?? null,
    };
    return pc;
  });
  const createdPcById = mapKeyBy(createdPcs, (pc) => pc.id);
  const createdOH = createdOffHires.map((id, i): DefinedOffHire => {
    const created = createOffHires?.[i];
    assertDefined(created, 'created');
    // noinspection UnnecessaryLocalVariableJS
    const oh: DefinedOffHire = {
      ...created,
      archived: created.archived ?? false,
      clientId: assertedDefined(created.clientId, 'createOffHires clientId'),
      meta: created.meta ?? '',
      type: created.type ?? OffHireType.DRY_DOCK,
      updatedAt: created.updatedAt ?? null,
      vesselId: created.vesselId ?? null,
      __typename: 'OffHire' as const,
      id: id.id as OffHireId,
      locationByLocationAId: locationsById.get(created.locationAId) ?? null,
      locationByLocationBId: locationsById.get(created.locationBId) ?? null,
      tcOutRate: created.tcOutRate ?? null,
      commissionPct: created.commissionPct ?? null,
      ballastBonus: created.ballastBonus ?? null,
      ballastBonusCommissionable: created.ballastBonusCommissionable ?? false,
    };
    return oh;
  });

  const createdS = createdShipments.map((id, i): DefinedShipment => {
    const created = createShipments?.at(i);
    assertDefined(created, 'created');
    assertDefined(created.clientId, 'createShipments clientId');
    // noinspection UnnecessaryLocalVariableJS
    const s: DefinedShipment = {
      __typename: 'Shipment' as const,
      id: id.id as ShipmentId,
      clientId: created.clientId,
      name: created.name,
      remarks: created.remarks ?? '',
      externalShipmentId: created.externalShipmentId ?? '',
      shipmentExternalStatusesByShipmentId: {
        __typename: 'ShipmentExternalStatusesConnection',
        nodes: [],
      },
      voyageAttributeId: created.voyageAttributeId ?? null,
      shipmentFreightByShipmentId: null,
      shipmentExtraFreightsByShipmentId: {
        __typename: 'ShipmentExtraFreightsConnection' as const,
        nodes: [],
      },
    };
    return s;
  });

  type ShipmentFreightWithShipmentId = {
    __typename: 'ShipmentFreight';
    id: ShipmentFreightId;
    clientId: string;
    shipmentId: ShipmentId;
    freightAmount: string | number;
    freightUnit: PaymentUnit;
  };

  type ShipmentExtraFreightWithShipmentId = {
    __typename: 'ShipmentExtraFreight';
    id: ShipmentExtraFreightId;
    clientId: string;
    shipmentId: ShipmentId;
    name: string | null;
    freightAmount: string | number;
    freightUnit: PaymentUnit;
    description: string | null;
  };

  const createdSF = createdShipmentFreights.map(
    ({ id, shipment_id }, i): ShipmentFreightWithShipmentId => {
      const created = createShipmentFreights?.at(i);
      assertDefined(created, 'created');

      return {
        __typename: 'ShipmentFreight',
        id: id as ShipmentFreightId,
        clientId: assertedDefined(
          created.clientId,
          'createShipmentFreights clientId',
        ),
        shipmentId: shipment_id as ShipmentId,
        freightAmount: created.freightAmount,
        freightUnit: created.freightUnit.toUpperCase() as PaymentUnit,
      };
    },
  );

  const createdSFE = createdShipmentExtraFreights.map(
    ({ id, shipment_id }, i): ShipmentExtraFreightWithShipmentId => {
      const created = createShipmentExtraFreights?.at(i);
      assertDefined(created, 'created');
      return {
        __typename: 'ShipmentExtraFreight',
        id: id as ShipmentExtraFreightId,
        clientId: assertedDefined(
          created.clientId,
          'createShipmentExtraFreights clientId',
        ),
        shipmentId: shipment_id as ShipmentId,
        name: created.name ?? null,
        freightAmount: created.freightAmount,
        freightUnit: created.freightUnit.toUpperCase() as PaymentUnit,
        description: created.description ?? null,
      };
    },
  );

  const createdSFByShipmentId = mapKeyBy(createdSF, (sf) =>
    String(sf.shipmentId),
  );
  const createdSFEByShipmentId = new Map<
    string,
    ShipmentExtraFreightWithShipmentId[]
  >();
  for (const sfe of createdSFE) {
    const shipmentIdStr = String(sfe.shipmentId);
    const existing = createdSFEByShipmentId.get(shipmentIdStr);
    if (existing) {
      existing.push(sfe);
    } else {
      createdSFEByShipmentId.set(shipmentIdStr, [sfe]);
    }
  }

  const updatedSFById = mapKeyBy(updateShipmentFreights ?? [], (u) => u.id);
  const updatedSFEById = mapKeyBy(
    updateShipmentExtraFreights ?? [],
    (u) => u.id,
  );
  const deletedSFSet = new Set(deleteShipmentFreights?.map((id) => id) ?? []);
  const deletedSFESet = new Set(
    deleteShipmentExtraFreights?.map((id) => id) ?? [],
  );

  const updateSOById = mapKeyBy(updateScheduledOrders ?? [], (u) => u.id);
  const updateHBSById = mapKeyBy(updateHandledBySpots ?? [], (u) => u.id);

  const fields: Required<{
    [FieldName in keyof Omit<PlanningPageQuery, 'allOrders'>]: (
      field: PlanningPageQuery[FieldName],
    ) => PlanningPageQuery[FieldName];
  }> & {
    allOrders: (
      field: PlanningPageQuery['allOrders'],
      allPortCalls: PlanningPageQuery['allPortCalls'],
    ) => PlanningPageQuery['allOrders'];
  } = {
    allShips: (a) => a,
    __typename: (a) => a,
    allCoaContracts: (a) => a,
    allOrganizations: (a) => a,
    allZones: (a) => a,
    allAssortments: (a) => a,
    allPortCostsByPortAndVesselNames: (a) => a,
    allPortCostsByPortAndPrimaryAttributes: (a) => a,
    allLatestPortCostByPortAndPrimaryAttributes: (a) => a,
    allChannelCostEstimates: (a) => a,
    allPortCostDefaults: (a) => a,
    allScheduledOrders: (allScheduledOrders) => {
      if (!allScheduledOrders) {
        if (!createdScheduledOrders) {
          return allScheduledOrders;
        }
        return {
          __typename: 'ScheduledOrdersConnection' as const,
          nodes: createdSOs,
        };
      }
      const { nodes } = allScheduledOrders;
      if (deleteScheduledOrders?.length) {
        allScheduledOrders = {
          ...allScheduledOrders,
          nodes: nodes.filter((node) => {
            const id = node?.id;
            return id && !deleteScheduledOrders.includes(id);
          }),
        };
      }
      if (updateScheduledOrders?.length) {
        allScheduledOrders = {
          ...allScheduledOrders,
          nodes: compact(allScheduledOrders.nodes).map(
            (node: DefinedScheduledOrder): DefinedScheduledOrder => {
              const id = node.id;
              const u = updateSOById.get(id)?.scheduledOrderPatch;
              return u
                ? ({
                    ...node,
                    ...u,
                    orderId: u.orderId ?? node.orderId,
                    loadingStart: u.loadingStart ?? node.loadingStart,
                    loadingEnd: u.loadingEnd ?? node.loadingEnd,
                    loadingDuration: u.loadingDuration ?? node.loadingDuration,
                    dischargingStart:
                      u.dischargingStart ?? node.dischargingStart,
                    dischargingEnd: u.dischargingEnd ?? node.dischargingEnd,
                    dischargingDuration:
                      u.dischargingDuration ?? node.dischargingDuration,
                    loadingConcurrent:
                      u.loadingConcurrent ?? node.loadingConcurrent,
                    dischargingConcurrent:
                      u.dischargingConcurrent ?? node.dischargingConcurrent,
                    shared: u.shared ?? node.shared,
                    clientId: u.clientId ?? node.clientId,
                    cleaningOmitted: u.cleaningOmitted ?? node.cleaningOmitted,
                  } satisfies DefinedScheduledOrder)
                : node;
            },
          ),
        };
      }
      if (createdScheduledOrders?.length) {
        allScheduledOrders = {
          ...allScheduledOrders,
          nodes: [...allScheduledOrders.nodes, ...createdSOs],
        };
      }
      return allScheduledOrders;
    },
    allHandledByCoas: (value) => {
      if (!value) {
        if (!createdHandledByCoas) {
          return value;
        }
        return {
          __typename: 'HandledByCoasConnection' as const,
          nodes: createdHBCs,
        };
      }
      const { nodes } = value;
      if (deleteHandledByCoas?.length) {
        value = {
          ...value,
          nodes: nodes.filter((node) => {
            const id = node?.id;
            return id && !deleteHandledByCoas.includes(id);
          }),
        };
      }
      if (updateHandledByCoas?.length) {
        const update = mapKeyBy(updateHandledByCoas, (u) => u.id);
        value = {
          ...value,
          nodes: compact(value.nodes).map(
            (node: DefinedHandledByCoa): DefinedHandledByCoa => {
              const id = node.id;
              const u = update.get(id)?.handledByCoaPatch;
              return u
                ? {
                    ...node,
                    ...u,
                    coaId: u.coaId ?? node.coaId,
                    orderId: u.orderId ?? node.orderId,
                    loadingStart: u.loadingStart ?? node.loadingStart,
                    loadingEnd: u.loadingEnd ?? node.loadingEnd,
                    loadingDuration: u.loadingDuration ?? node.loadingDuration,
                    dischargingStart:
                      u.dischargingStart ?? node.dischargingStart,
                    dischargingEnd: u.dischargingEnd ?? node.dischargingEnd,
                    dischargingDuration:
                      u.dischargingDuration ?? node.dischargingDuration,
                    loadingConcurrent:
                      u.loadingConcurrent ?? node.loadingConcurrent,
                    dischargingConcurrent:
                      u.dischargingConcurrent ?? node.dischargingConcurrent,
                    shared: u.shared ?? node.shared,
                    clientId: u.clientId ?? node.clientId,
                  }
                : node;
            },
          ),
        };
      }
      if (createdHandledByCoas?.length) {
        value = {
          ...value,
          nodes: [...value.nodes, ...createdHBCs],
        };
      }
      return value;
    },
    allHandledBySpots: (value) => {
      if (!value) {
        if (!createdHandledBySpots) {
          return value;
        }
        return {
          __typename: 'HandledBySpotsConnection' as const,
          nodes: createdHBSs,
        };
      }
      const { nodes } = value;
      if (deleteHandledBySpots?.length) {
        value = {
          ...value,
          nodes: nodes.filter((node) => {
            const id = node?.id;
            return id && !deleteHandledBySpots.includes(id);
          }),
        };
      }
      if (updateHandledBySpots?.length) {
        value = {
          ...value,
          nodes: compact(value.nodes).map(
            (node: DefinedHandledBySpot): DefinedHandledBySpot => {
              const id = node.id;
              const u = updateHBSById.get(id)?.handledBySpotPatch;
              return u
                ? {
                    ...node,
                    ...u,
                    orderId: u.orderId ?? node.orderId,
                    loadingStart: u.loadingStart ?? node.loadingStart,
                    loadingEnd: u.loadingEnd ?? node.loadingEnd,
                    loadingDuration: u.loadingDuration ?? node.loadingDuration,
                    dischargingStart:
                      u.dischargingStart ?? node.dischargingStart,
                    dischargingEnd: u.dischargingEnd ?? node.dischargingEnd,
                    dischargingDuration:
                      u.dischargingDuration ?? node.dischargingDuration,
                    loadingConcurrent:
                      u.loadingConcurrent ?? node.loadingConcurrent,
                    dischargingConcurrent:
                      u.dischargingConcurrent ?? node.dischargingConcurrent,
                    shared: u.shared ?? node.shared,
                    clientId: u.clientId ?? node.clientId,
                  }
                : node;
            },
          ),
        };
      }
      if (createdHandledBySpots?.length) {
        value = {
          ...value,
          nodes: [...value.nodes, ...createdHBSs],
        };
      }
      return value;
    },
    allPortCalls: (value) => {
      if (!value) {
        if (!createdPortCalls) {
          return value;
        }
        return {
          __typename: 'PortCallsConnection' as const,
          nodes: createdPcs,
        };
      }
      let { nodes } = value;
      if (deletePortCalls?.length) {
        nodes = nodes.filter((node) => {
          const id = node?.id;
          return id && !deletePortCalls.includes(id);
        });
        value = {
          ...value,
          nodes: nodes,
        };
      }
      if (updatePortCalls?.length) {
        const update = mapKeyBy(updatePortCalls, (u) => u.id);
        nodes = compact(nodes).map((node: DefinedPortCall): DefinedPortCall => {
          const id = node.id;
          const u = update.get(id)?.portCallPatch;
          const locationId = u?.locationId ?? node.locationId;
          const previousLocationId =
            u?.previousLocationId ?? node.previousLocationId;
          return u
            ? ({
                ...node,
                ...u,
                clientId: u.clientId ?? node.clientId,
                legAvoidsChannels:
                  u.legAvoidsChannels ?? node.legAvoidsChannels,
                archived: u.archived ?? node.archived,
                useHistoricalPortCost:
                  u.useHistoricalPortCost ?? node.useHistoricalPortCost,
                useLatestHistoricalPortCost:
                  u.useLatestHistoricalPortCost ??
                  node.useLatestHistoricalPortCost,
                useOneTimeOverrideCost: u.useOneTimeOverrideCost ?? false,
                useIntegrationPortCost: u.useIntegrationPortCost ?? false,
                locationId: locationId,
                previousLocationId: previousLocationId,
                locationByLocationId: locationsById.get(locationId) ?? null,
                weatherFactor: u.weatherFactor ?? node.weatherFactor ?? null,
              } satisfies DefinedPortCall)
            : node;
        });
        value = {
          ...value,
          nodes: nodes,
        };
      }
      if (createdPortCalls?.length) {
        value = {
          ...value,
          nodes: [...nodes, ...createdPcs],
        };
      }
      return value;
    },
    allSingleEvents: (value) => {
      if (!value) {
        if (!createdSingleEvents) {
          return value;
        }
        return {
          __typename: 'SingleEventsConnection' as const,
          nodes: createdSE,
        };
      }
      let { nodes } = value;
      if (deleteSingleEvents?.length) {
        nodes = nodes.filter((node) => {
          const id = node?.id;
          return id && !deleteSingleEvents.includes(id);
        });
        value = {
          ...value,
          nodes,
        };
      }
      if (updateSingleEvents?.length) {
        const update = mapKeyBy(updateSingleEvents, (u) => u.id);
        nodes = compact(nodes).map(
          (node: DefinedSingleEvent): DefinedSingleEvent => {
            const id = node.id;
            const u = update.get(id)?.singleEventPatch;
            const locationId = u?.locationId ?? node.locationId;
            return u
              ? ({
                  ...node,
                  ...u,
                  clientId: u.clientId ?? node.clientId,
                  duration: u.duration ?? node.duration,
                  end: u.end ?? node.end,
                  locked: u.locked ?? node.locked,
                  shared: u.shared ?? node.shared,
                  start: u.start ?? node.start,
                  concurrent: u.concurrent ?? node.concurrent,
                  archived: u.archived ?? node.archived,
                  locationId: locationId,
                  locationByLocationId: locationsById.get(locationId) ?? null,
                  type: u.type ?? node.type,
                  id,
                } satisfies DefinedSingleEvent)
              : node;
          },
        );
        value = {
          ...value,
          nodes: nodes,
        };
      }
      if (createdSingleEvents?.length) {
        nodes = [...nodes, ...createdSE];
        value = {
          ...value,
          nodes: nodes,
        };
      }
      if (createdRelationById.size || deleteSingleEventPortCallRelations) {
        value = {
          ...value,
          nodes: compact(nodes).map(
            (node: DefinedSingleEvent): DefinedSingleEvent => {
              const id = node.id;
              const rel = createdRelationById.get(id);
              const old = node.singleEventPortCallRelationsBySingleEventId;
              const rels = old?.nodes;
              const newRels = rels?.filter(
                (n) =>
                  !(n && deleteSingleEventPortCallRelations?.includes(n.id)),
              );
              return rel
                ? {
                    ...node,
                    singleEventPortCallRelationsBySingleEventId: {
                      ...old,
                      nodes: [rel],
                    },
                  }
                : old && newRels && newRels.length !== rels?.length
                  ? {
                      ...node,
                      singleEventPortCallRelationsBySingleEventId: {
                        ...old,
                        nodes: newRels,
                      },
                    }
                  : node;
            },
          ),
        };
      }
      return value;
    },
    allOffHires: (value) => {
      if (!value) {
        if (!createdOffHires) {
          return value;
        }
        return {
          __typename: 'OffHiresConnection' as const,
          nodes: createdOH,
        };
      }
      const { nodes } = value;
      if (deleteOffHires?.length) {
        value = {
          ...value,
          nodes: nodes.filter((node) => {
            const id = node?.id;
            return id && !deleteOffHires.includes(id);
          }),
        };
      }
      if (updateOffHires?.length) {
        const update = mapKeyBy(updateOffHires, (u) => u.id);
        value = {
          ...value,
          nodes: compact(value.nodes).map(
            (node: DefinedOffHire): DefinedOffHire => {
              const id = node.id;
              const u = update.get(id)?.offHirePatch;
              const locationAId = u?.locationAId ?? node.locationAId;
              const locationBId = u?.locationBId ?? node.locationBId;
              return u
                ? {
                    ...node,
                    ...u,
                    end: u.end ?? node.end,
                    locked: u.locked ?? node.locked,
                    shared: u.shared ?? node.shared,
                    start: u.start ?? node.start,
                    planningOrganisationId:
                      u.planningOrganisationId ?? node.planningOrganisationId,
                    userId: u.userId ?? node.userId,
                    clientId: u.clientId ?? node.clientId,
                    archived: u.archived ?? node.archived,
                    duration: u.duration ?? node.duration,
                    locationAId,
                    locationBId,
                    locationByLocationAId:
                      locationsById.get(locationAId) ?? null,
                    locationByLocationBId:
                      locationsById.get(locationBId) ?? null,
                  }
                : node;
            },
          ),
        };
      }
      if (createdOffHires?.length) {
        value = {
          ...value,
          nodes: [...value.nodes, ...createdOH],
        };
      }
      return value;
    },
    allOrders: (value, allPortCalls) => {
      if (!value) {
        return value;
      }
      const createdCCAByOrderId = mapKeyBy2(
        createCargoCompartmentAllocations?.map(
          (cca, i): DefinedCargoCompartmentAllocation => {
            const {
              orderId,
              scheduledOrderClientId,
              clientId,
              compartmentId,
              amount,
            } = cca;
            return {
              __typename: 'CargoCompartmentAllocation' as const,
              id: createdCargoCompartmentAllocations[i]
                .id as CargoCompartmentAllocationId,
              orderId: orderId as OrderId,
              scheduledOrderClientId,
              clientId,
              compartmentId: compartmentId as CargoCompartmentId,
              amount,
            };
          },
        ) ?? [],
        getOrderId,
        getCompartmentId,
      );
      const createdOPCRByOrderIdAndPCId = mapKeyBy2(
        createOrderPortCallRelations?.map(
          (opcr, i): DefinedOrderPortCallRelation => {
            const { orderId, portCallId, isLoadPort } = opcr;
            const pcId = getId(portCallId, createdPortCalls) as PortCallId;
            const portCallByPortCallId =
              createdPcById.get(pcId) ??
              allPortCalls?.nodes.find((pc) => pc?.id === pcId);
            assertDefined(portCallByPortCallId, 'portCallByPortCallId');
            return {
              __typename: 'OrderPortCallRelation' as const,
              id: createdOrderPortCallRelations[i]
                .id as OrderPortCallRelationId,
              orderId: orderId as OrderId,
              portCallId: pcId,
              isLoadPort,
              portCallByPortCallId,
            };
          },
        ) ?? [],
        getOrderId,
        getPortCallId,
      );
      const createdVCOFSByOVCRId = mapKeyBy(
        createVoyageCharterOrderFreights?.map(
          (
            f,
            i,
          ): Required<Costs_FreightFragment> &
            Required<VoyageIncome_VoyageCharterOrderFreightFragment> => {
            const { orderVoyageCharterRelationId, freightAmount, freightUnit } =
              f;
            return {
              __typename: 'VoyageCharterOrderFreight' as const,
              id: createdVoyageCharterOrderFreights[i]
                .id as VoyageCharterOrderFreightId,
              orderVoyageCharterRelationId: getId(
                orderVoyageCharterRelationId,
                createdOrderVoyageCharterRelations,
              ) as OrderVoyageCharterRelationId,
              freightAmount: freightAmount ?? null,
              freightUnit: freightUnit ?? null,
            };
          },
        ) ?? [],
        getOrderVoyageCharterRelationId,
      );
      const createdOVCRByOrderIdAndVCId = mapKeyBy(
        createOrderVoyageCharterRelations?.map(
          (ovcr, i): DefinedOrderVoyageCharterRelation => {
            const { orderId, coaRouteId, voyageCharterId, outgoing } = ovcr;
            const id = createdOrderVoyageCharterRelations[i]
              .id as OrderVoyageCharterRelationId;
            const vcof = createdVCOFSByOVCRId.get(id);
            return {
              __typename: 'OrderVoyageCharterRelation' as const,
              id: id,
              orderId: orderId as OrderId,
              coaRouteId: (coaRouteId as CoaContractPortId) ?? null,
              voyageCharterId: getId(
                voyageCharterId,
                createdVoyageCharters,
              ) as VoyageCharterId,
              outgoing: outgoing ?? true,
              // TODO check if more data needs propagation
              voyageCharterOrderFreightsByOrderVoyageCharterRelationId: {
                __typename: 'VoyageCharterOrderFreightsConnection',
                nodes: vcof ? [vcof] : [],
              },
              voyageCharterByVoyageCharterId: null,
            };
          },
        ) ?? [],
        getOrderId,
      );

      const coaById = mapKeyBy(
        dataInStore.allCoaContracts?.nodes ?? [],
        (coa) => coa?.id,
      );

      const createdVCTCByVCId = mapKeyBy(
        createVoyageCharterToContracts?.map(
          (
            vctc,
            i,
          ): Required<ScenarioTimeline_VoyageCharterToContractFragment> &
            DefinedVoyageCharterToContract => {
            const { coaId, voyageCharterId } = vctc;
            return {
              __typename: 'VoyageCharterToContract' as const,
              id: createdVoyageCharterToContracts[i]
                .id as VoyageCharterToContractId,
              voyageCharterId: getId(
                voyageCharterId,
                createdVoyageCharters,
              ) as VoyageCharterId,
              coaId: (coaId as CoaContractId) ?? null,
              coaContractByCoaId:
                (coaId && coaById.get(coaId as CoaContractId)) || null,
            };
          },
        ) ?? [],
        getVoyageCharterId,
      );

      if (updateOrders?.length) {
        const update = mapKeyBy(
          updateOrders.map((u) => {
            const { id, clientMutationId } = u;
            const { shipmentId, ...patch } = u.orderPatch;
            const orderPatch = patch as OrderPatch;
            if (shipmentId !== undefined) {
              orderPatch.shipmentId =
                shipmentId &&
                (getId(shipmentId, createdShipments) as ShipmentId);
            }
            return {
              id,
              clientMutationId,
              orderPatch,
            };
          }),
          (u) => u.id,
        );
        value = {
          ...value,
          nodes: compact(value.nodes).map(
            (node: DefinedOrder): DefinedOrder => {
              const id = node.id;
              const u = update.get(id)?.orderPatch;
              return u
                ? {
                    ...node,
                    ...u,
                    archived: u.archived ?? node.archived,
                    dischargePortId: u.dischargePortId ?? node.dischargePortId,
                    cargoAmount: u.cargoAmount ?? node.cargoAmount,
                    toBeDeleted: u.toBeDeleted ?? node.toBeDeleted ?? false,
                  }
                : node;
            },
          ),
        };
      }
      if (
        !deleteOrderPortCallRelations?.length &&
        !deleteVoyageCharterToContracts?.length &&
        !deleteOrderVoyageCharterRelations?.length &&
        !updateCargoCompartmentAllocations?.length &&
        !deleteScheduledOrders?.length &&
        !updateScheduledOrders?.length &&
        !createScheduledOrders?.length &&
        !deleteHandledBySpots?.length &&
        !updateHandledBySpots?.length &&
        !createHandledBySpots?.length &&
        createdOPCRByOrderIdAndPCId.size === 0 &&
        createdOVCRByOrderIdAndVCId.size === 0 &&
        createdCCAByOrderId.size === 0
      ) {
        return value;
      }
      const updateCCAById = mapKeyBy(
        updateCargoCompartmentAllocations ?? [],
        (ucca) => ucca.id,
      );
      return {
        ...value,
        nodes: compact(value.nodes).map((node: DefinedOrder): DefinedOrder => {
          const id = node.id;

          const opcrs = createdOPCRByOrderIdAndPCId.get(id);
          const ovcr = createdOVCRByOrderIdAndVCId.get(id);
          const ccas = createdCCAByOrderId.get(id);

          const ccaNodes = node.cargoCompartmentAllocationsByOrderId.nodes
            ?.filter(
              (cca) =>
                cca && !deleteCargoCompartmentAllocations?.includes(cca.id),
            )
            .map(
              (
                cca: DefinedCargoCompartmentAllocation,
              ): DefinedCargoCompartmentAllocation => {
                assertDefined(cca, 'cca');
                const { id } = cca;
                const u =
                  updateCCAById.get(id)?.cargoCompartmentAllocationPatch;
                return u
                  ? ({
                      ...cca,
                      ...u,
                      clientId: u.clientId ?? cca.clientId,
                      orderId: u.orderId ?? cca.orderId,
                      compartmentId: u.compartmentId ?? cca.compartmentId,
                      scheduledOrderClientId:
                        u.scheduledOrderClientId ?? cca.scheduledOrderClientId,
                      amount: u.amount ?? cca.amount,
                    } satisfies DefinedCargoCompartmentAllocation)
                  : cca;
              },
            );
          for (const cca of ccas.values()) {
            ccaNodes.push(cca);
          }

          const opcrNodes = node.orderPortCallRelationsByOrderId.nodes?.filter(
            (opcr) => opcr && !deleteOrderPortCallRelations?.includes(opcr.id),
          );
          for (const opcr of opcrs.values()) {
            opcrNodes.push(opcr);
          }

          let oovcr = node.outgoingVoyageCharterRelation;
          if (oovcr && deleteOrderVoyageCharterRelations?.includes(oovcr.id)) {
            oovcr = null;
          }
          if (ovcr) {
            oovcr = ovcr;
          }

          let iovcr = node.incomingVoyageCharterRelation;
          if (iovcr && deleteOrderVoyageCharterRelations?.includes(iovcr.id)) {
            iovcr = null;
          }
          if (iovcr) {
            const vcByVcId = iovcr.voyageCharterByVoyageCharterId;
            assertDefined(vcByVcId, 'voyageCharterByVoyageCharterId');

            const voyageCharterToContractsByVoyageCharterId =
              vcByVcId.voyageCharterToContractsByVoyageCharterId;
            const updateVCTC: DefinedVoyageCharterToContract[] =
              voyageCharterToContractsByVoyageCharterId.nodes
                .filter(
                  (vctc) =>
                    vctc && !deleteVoyageCharterToContracts?.includes(vctc.id),
                )
                .map(
                  (
                    vctc: DefinedVoyageCharterToContract,
                  ): DefinedVoyageCharterToContract => {
                    assertDefined(vctc, 'vctc');
                    const u = updateVoyageCharterToContracts?.find(
                      (u) => u.id === vctc.id,
                    )?.voyageCharterToContractPatch;
                    return {
                      ...vctc,
                      ...u,
                      voyageCharterId:
                        u?.voyageCharterId ?? vctc.voyageCharterId,
                    };
                  },
                );
            const vctcs = createdVCTCByVCId.get(iovcr.voyageCharterId);
            if (vctcs) {
              updateVCTC.push(vctcs);
            }

            const updateFreight =
              iovcr.voyageCharterOrderFreightsByOrderVoyageCharterRelationId.nodes
                .filter(
                  (f) => f && !deleteVoyageCharterOrderFreights?.includes(f.id),
                )
                .map(
                  (
                    f: DefinedVoyageCharterOrderFreight,
                  ): DefinedVoyageCharterOrderFreight => {
                    assertDefined(f, 'f');
                    const u = updateVoyageCharterOrderFreights?.find(
                      (u) => u.id === f.id,
                    )?.voyageCharterOrderFreightPatch;
                    return u
                      ? {
                          ...f,
                          ...u,
                          orderVoyageCharterRelationId:
                            u.orderVoyageCharterRelationId ??
                            f.orderVoyageCharterRelationId,
                        }
                      : f;
                  },
                );
            const freight = createdVCOFSByOVCRId.get(iovcr.id);
            if (freight) {
              updateFreight.push(freight);
            }

            const vcId = iovcr.voyageCharterId;
            const u = updateVoyageCharters?.find(
              (u) => u.id === vcId,
            )?.voyageCharterPatch;

            iovcr = {
              ...iovcr,
              voyageCharterByVoyageCharterId: {
                ...vcByVcId,
                voyageCharterToContractsByVoyageCharterId: {
                  ...voyageCharterToContractsByVoyageCharterId,
                  nodes: updateVCTC,
                },
                ...u,
                isInquiry: u?.isInquiry ?? vcByVcId.isInquiry,
                isPreliminary: u?.isPreliminary ?? vcByVcId.isPreliminary,
                isTemplate: u?.isTemplate ?? vcByVcId.isTemplate,
                deleteOnUnassign:
                  u?.deleteOnUnassign ?? vcByVcId.deleteOnUnassign,
              },
              voyageCharterOrderFreightsByOrderVoyageCharterRelationId: {
                ...iovcr.voyageCharterOrderFreightsByOrderVoyageCharterRelationId,
                nodes: updateFreight,
              },
            };
          }

          const sosByOId = node.scheduledOrdersByOrderId;
          let soNodes = sosByOId?.nodes
            ?.filter((so) => so && !deleteScheduledOrders?.includes(so.id))
            .filter(isDefined)
            .map((node: DefinedScheduledOrder): DefinedScheduledOrder => {
              const id = node.id;
              const u = updateSOById.get(id)?.scheduledOrderPatch;
              return u
                ? ({
                    ...node,
                    ...u,
                    orderId: u.orderId ?? node.orderId,
                    loadingStart: u.loadingStart ?? node.loadingStart,
                    loadingEnd: u.loadingEnd ?? node.loadingEnd,
                    loadingDuration: u.loadingDuration ?? node.loadingDuration,
                    dischargingStart:
                      u.dischargingStart ?? node.dischargingStart,
                    dischargingEnd: u.dischargingEnd ?? node.dischargingEnd,
                    dischargingDuration:
                      u.dischargingDuration ?? node.dischargingDuration,
                    loadingConcurrent:
                      u.loadingConcurrent ?? node.loadingConcurrent,
                    dischargingConcurrent:
                      u.dischargingConcurrent ?? node.dischargingConcurrent,
                    shared: u.shared ?? node.shared,
                    clientId: u.clientId ?? node.clientId,
                    cleaningOmitted: u.cleaningOmitted ?? node.cleaningOmitted,
                  } satisfies DefinedScheduledOrder)
                : node;
            });
          for (const so of createdSOs) {
            if (so.orderId === node.id) {
              if (!soNodes) {
                soNodes = [so];
              } else {
                soNodes.push(so);
              }
            }
          }

          let handledBySpotByOrderId = node.handledBySpotByOrderId;
          if (handledBySpotByOrderId?.orderId === node.id) {
            handledBySpotByOrderId = null;
          }
          const hbsId = handledBySpotByOrderId?.id;
          const uHBS = hbsId && updateHBSById.get(hbsId)?.handledBySpotPatch;
          handledBySpotByOrderId = uHBS
            ? {
                ...handledBySpotByOrderId,
                ...uHBS,
                id: hbsId,
              }
            : (handledBySpotByOrderId ??
              createdHBSs?.find((hbs) => hbs.orderId === node.id) ??
              null);

          const loadPortId = node.loadPortId;
          const dischargePortId = node.dischargePortId;
          const newValue: DefinedOrder = {
            ...node,
            coaId: node.coaId ?? null,
            assortmentByAssortmentId: node.assortmentByAssortmentId ?? null,
            cargoUnitByCargoUnitId: node.cargoUnitByCargoUnitId ?? null,
            locationByLoadPortId: loadPortId
              ? (locationsById.get(loadPortId) ?? null)
              : null,
            locationByDischargePortId: dischargePortId
              ? (locationsById.get(dischargePortId) ?? null)
              : null,
            __typename: 'Order' as const,
            handledBySpotByOrderId: handledBySpotByOrderId,
            cargoCompartmentAllocationsByOrderId: {
              ...node.cargoCompartmentAllocationsByOrderId,
              nodes: ccaNodes,
            },
            orderPortCallRelationsByOrderId: {
              ...node.orderPortCallRelationsByOrderId,
              nodes: opcrNodes,
            },
            outgoingVoyageCharterRelation: oovcr ?? null,
            incomingVoyageCharterRelation: iovcr ?? null,
            userByUserId: node.userByUserId ?? null,
          };
          if (soNodes) {
            newValue.scheduledOrdersByOrderId = {
              ...node.scheduledOrdersByOrderId,
              nodes: soNodes,
            };
          }
          return newValue;
        }),
      };
    },
    allShipments: (value) => {
      if (!value) {
        if (!createdShipments) {
          return value;
        }
        return {
          __typename: 'ShipmentsConnection' as const,
          nodes: createdS.map((shipment) => {
            const shipmentIdStr = String(shipment.id);
            const freight = createdSFByShipmentId.get(shipmentIdStr);
            const extraFreights = createdSFEByShipmentId.get(shipmentIdStr);
            return {
              ...shipment,
              shipmentFreightByShipmentId: freight ?? null,
              shipmentExtraFreightsByShipmentId: {
                ...shipment.shipmentExtraFreightsByShipmentId,
                nodes: extraFreights ?? [],
              },
            };
          }),
        };
      }

      const hasFreightUpdates =
        createdSF.length ||
        createdSFE.length ||
        updatedShipmentFreights.length ||
        updatedShipmentExtraFreights.length ||
        deletedShipmentFreights.length ||
        deletedShipmentExtraFreights.length;

      if (hasFreightUpdates) {
        value = {
          ...value,
          nodes: compact(value.nodes).map((shipment) => {
            const shipmentIdStr = String(shipment.id);
            const newFreight = createdSFByShipmentId.get(shipmentIdStr);
            const newExtraFreights = createdSFEByShipmentId.get(shipmentIdStr);

            let shipmentFreight = shipment.shipmentFreightByShipmentId;
            if (shipmentFreight) {
              if (deletedSFSet.has(shipmentFreight.id)) {
                shipmentFreight = null;
              } else {
                const update = updatedSFById.get(shipmentFreight.id);
                if (update?.shipmentFreightPatch) {
                  const patch = update.shipmentFreightPatch;
                  shipmentFreight = {
                    ...shipmentFreight,
                    freightAmount:
                      patch.freightAmount ?? shipmentFreight.freightAmount,
                    freightUnit:
                      (patch.freightUnit?.toUpperCase() as PaymentUnit) ??
                      (shipmentFreight.freightUnit.toUpperCase() as PaymentUnit),
                  };
                }
              }
            }
            if (newFreight) {
              shipmentFreight = newFreight;
            }

            let extraFreightNodes =
              shipment.shipmentExtraFreightsByShipmentId.nodes
                .filter((node) => node && !deletedSFESet.has(node.id))
                .map((node) => {
                  if (!node) return node;
                  const update = updatedSFEById.get(node.id);
                  if (update?.shipmentExtraFreightPatch) {
                    const patch = update.shipmentExtraFreightPatch;
                    return {
                      ...node,
                      name: patch.name ?? node.name,
                      freightAmount: patch.freightAmount ?? node.freightAmount,
                      freightUnit:
                        (patch.freightUnit?.toUpperCase() as PaymentUnit) ??
                        node.freightUnit,
                      description: patch.description ?? node.description,
                    };
                  }
                  return node;
                });
            if (newExtraFreights) {
              extraFreightNodes = [...extraFreightNodes, ...newExtraFreights];
            }

            return {
              ...shipment,
              __typename: 'Shipment',
              shipmentFreightByShipmentId: shipmentFreight ?? null,
              shipmentExtraFreightsByShipmentId: {
                ...shipment.shipmentExtraFreightsByShipmentId,
                nodes: extraFreightNodes,
              },
            };
          }),
        };
      }

      if (createdShipments?.length) {
        value = {
          ...value,
          nodes: [
            ...value.nodes,
            ...createdS.map((shipment) => {
              const shipmentIdStr = String(shipment.id);
              const freight = createdSFByShipmentId.get(shipmentIdStr);
              const extraFreights = createdSFEByShipmentId.get(shipmentIdStr);
              return {
                ...shipment,
                shipmentFreightByShipmentId: freight ?? null,
                shipmentExtraFreightsByShipmentId: {
                  ...shipment.shipmentExtraFreightsByShipmentId,
                  nodes: extraFreights ?? [],
                },
              };
            }),
          ],
        };
      }
      return value;
    },
    lastCommit: (value) => {
      if (!value) {
        return value;
      }
      const lastCommit: DefinedCommitLog = {
        __typename: 'CommitLog' as const,
        committedBy: userId,
        committedAt,
        id,
        scenarioClientId: scenarioClientId ?? scenarioCID ?? null,
        masterClientId:
          masterClientId ?? masterScenarioCommitLogClientId ?? null,
        userByCommittedBy: userByCommittedBy ??
          userById.get(userId) ?? {
            __typename: 'User' as const,
            id: userId,
            name: userId.toString(),
          },
        commitLogScenarioByMasterClientId:
          commitLogScenarioByMasterClientId ?? null,
      };
      const nodes: LastCommitNode[] = [lastCommit];
      return {
        ...value,
        __typename: 'CommitLogsConnection' as const,
        nodes: nodes,
      };
    },
  };
  const allPortCalls = fields.allPortCalls(dataInStore.allPortCalls);
  const newData = {
    ...dataInStore,
    allScheduledOrders: fields.allScheduledOrders(
      dataInStore.allScheduledOrders,
    ),
    allHandledByCoas: fields.allHandledByCoas(dataInStore.allHandledByCoas),
    allHandledBySpots: fields.allHandledBySpots(dataInStore.allHandledBySpots),
    allPortCalls: allPortCalls,
    allSingleEvents: fields.allSingleEvents(dataInStore.allSingleEvents),
    allOffHires: fields.allOffHires(dataInStore.allOffHires),
    allOrders: fields.allOrders(dataInStore.allOrders, allPortCalls),
    allShipments: fields.allShipments(dataInStore.allShipments),
    lastCommit: fields.lastCommit(dataInStore.lastCommit),
  };
  client.writeQuery({
    query: PlanningPageDocument,
    data: newData,
    variables,
  });

  if (userId) {
    const userName =
      userId === currentUserId ? null : userById.get(userId)?.name;
    const content = userName
      ? `Main scenario updated by ${userName}`
      : 'Main scenario updated';
    toast.info(content);
  }
}

const queuedCommitLogEntries: [number, () => void][] = [];
const onFinishCommit = () => {
  queuedCommitLogEntries.sort((a, b) => a[0] - b[0]);
  queuedCommitLogEntries.forEach((queued) => queued[1]());
  queuedCommitLogEntries.length = 0;
};

export const useDefined = <Nodes>(
  nodes: (Nodes | null | undefined)[] | undefined | null,
): Array<NonNullable<Nodes>> => useMemo(() => compact(nodes), [nodes]);

export type ScenarioTimingInputs = {
  isShipOwner?: boolean;
  skipLocalScenarios?: boolean;
  onlyFetchCommittedData?: boolean;
  autoUpdateUserScenarios?: boolean;
  ignoreShipownerTimelines?: boolean;
  useDbValues?: boolean;
};

export type ScenarioData<T extends ScenarioSchedule = ScenarioSchedule> = {
  spotEventGroups: SpotEventGroup[];
  coaEventGroups: CoaEventGroup[];
  timelines: EventTimeline[];
  spotEntries: readonly SpotEntry[];
  coaEntries: readonly CoaEntry[];
  scenario: T;
  tcTimeDataByVessel: Map<Ships['id'], TcTimeDataGroup>;
  eventsByVessel: VesselToSortedEvents;
  scenarioOrderById: Map<OrderId, ScenarioOrderWithShipmentId>;
  scenarioPortCallByClientId: Map<ClientId, PortCallEntry>;
};

export const dummyScenarioData: ScenarioData<InternalInitialSchedule> = {
  coaEntries: [],
  coaEventGroups: [],
  eventsByVessel: new Map(),
  scenario: dummyScenario,
  scenarioOrderById: new Map(),
  scenarioPortCallByClientId: new Map(),
  spotEntries: [],
  spotEventGroups: [],
  tcTimeDataByVessel: new Map(),
  timelines: [],
};

export type GetScenarioData = <T extends ScenarioSchedule>(
  scenario: T,
) => ScenarioData<T>;

export type CompletedData = { timelines: EventTimeline[] };

export type ComputedScenarioTimings = {
  masterData: ScenarioData<MasterSchedule | InternalInitialSchedule>;
  distances: Distances;
  noGoZones: string;
  committedOrderById: Map<number, ScenarioTimeline_OrderFragment>;
  offHires: Array<ScenarioTimeline_OffHireFragment>;
  getScenarioData: GetScenarioData;
  committedData: CommittedPlanningData;
  data: PlanningPageQuery | undefined;
  portCostEstimateMaps: PortCostEstimateMaps;
  loading: boolean;
  coas: Array<NonNullable<ScenarioTimeline_CoaFragment>>;
  organizationsById: Map<number, ScenarioTimeline_OrganizationFragment>;
  singleEvents: Array<ScenarioTimeline_SingleEventFragment>;
  showArchived: boolean;
  setShowArchived: (value: ((prevState: boolean) => boolean) | boolean) => void;
  portIds: Set<LocationId>;
  selectedScenarioData: ScenarioData<
    | (ScenarioSchedule & {
        estimateTimelines: undefined;
      })
    | InternalInitialSchedule
  >;
  estimateTimelinesData: EstimateTimelineData[] | undefined;
  handledByCoas: Array<ScenarioTimeline_CoaAssignmentFragment>;
  vessels: Array<Vessel>;
  useScenariosResult: UseScenariosResult;
  calculationConfig: CalculationConfig;
  refetchPlanningPageQuery: () => Promise<unknown>;
  scheduleContext: ScheduleDataContext;
  collapsedTimelines: readonly [
    Set<EventTimeline['id']>,
    (
      value:
        | ((prevState: Set<EventTimeline['id']>) => Set<EventTimeline['id']>)
        | Set<EventTimeline['id']>,
    ) => void,
  ];
  completedData: CompletedData;
  completedLimit: Date;
  setCompletedLimit: (value: Date) => void;
  configUseCalculatedLimit: boolean;
};

const initialCommittedData: CommittedPlanningData = {
  completedOrderIds: new Set(),
  completedOffHireIds: new Set(),
  completedSingleEventIds: new Set(),
  orders: [],
  committedOrderById: new Map<number, ScenarioTimeline_OrderFragment>(),
  orderPortCallRelationsByOrderId: new DefaultMap<
    number,
    Map<boolean, OrderPortCallRelation | null>
  >(() => new Map()),
  cargoAllocations: new Map<
    ClientId,
    WithDbId<CargoAllocation, CargoCompartmentAllocationId>
  >(),
  scheduledEntries: [],
  tcOrderEntries: new Map<ClientId, WithDbId<TCOrderEntry, ScheduledOrderId>>(),
  completedEntries: [],
  completedTcOrderEntries: new Map<
    ClientId,
    WithDbId<TCOrderEntry, ScheduledOrderId>
  >(),
  handledByCoas: [],
  coaEntries: new Map<ClientId, WithDbId<CoaEntry, HandledByCoaId>>(),
  handledBySpots: [],
  spotEntries: new Map<ClientId, WithDbId<SpotEntry, HandledBySpotId>>(),
  offHires: [],
  offHireEntries: [],
  offHireEntriesByClientId: new Map<
    ClientId,
    WithDbId<OffHireEntry, OffHireId>
  >(),
  portCallFragments: [],
  portCallFragmentsByDbId: new Map<
    number,
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >(),
  portCallEntriesByClientId: new Map<
    ClientId,
    WithDbId<PortCallEntry, PortCallId>
  >(),
  portCallEntriesByDbId: new Map<number, WithDbId<PortCallEntry, PortCallId>>(),
  singleEvents: [],
  singleEventPortCallRelationsBySingleEventId: new Map<
    number,
    SingleEventPortCallRelation
  >(),
  singleEventEntries: [],
  singleEventEntriesByClientId: new Map<
    ClientId,
    WithDbId<SingleEntry, SingleEventId>
  >(),
  shipmentByClientId: new Map<ClientId, PersistedShipment>(),
  scheduleData: [],
  allScheduleData: [],
  committedPortIds: [],
  portCallsByClientId: new Map<
    ClientId,
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >(),
  vessels: [],
  vesselsById: new Map<number, Vessel>(),
  coas: [],
  organizationsById: new Map<number, ScenarioTimeline_OrganizationFragment>(),
  currentUserOrganization: null,
  lastCommit: [],
};
const defaultInputs: ScenarioTimingInputs = {
  autoUpdateUserScenarios: false,
  ignoreShipownerTimelines: false,
  onlyFetchCommittedData: false,
  skipLocalScenarios: false,
  isShipOwner: false,
  useDbValues: false,
};

const isWdycDisabled = !(
  typeof window !== 'undefined' && window.location.search.includes('wdyc')
);

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

// Why did you change?
export function useWdyc<
  Value,
  Deps extends { [s: string]: unknown } | ArrayLike<unknown>,
>(
  value: Value,
  deps: Deps,
  onChange: (changes: {
    [key in keyof Deps]: [prev: Deps[key], next: Deps[key]];
  }) => void = console.log,
) {
  if (isWdycDisabled) return;
  // Condition is constant over re-renders, this rules-of-hooks does not apply
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const val = useRef(value);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dep = useRef(deps);
  if (!Object.is(val.current, value)) {
    const prev = dep.current;
    val.current = value;
    dep.current = deps;

    const entries = Object.entries(deps) as Entries<Deps>;

    const changedEntries = entries.filter(([k, v]) => prev[k] !== v);

    const diff = changedEntries.map(
      ([k, v]) => [k, [prev[k], v] as const] as const,
    );

    type Changes = { [key in keyof Deps]: [prev: Deps[key], next: Deps[key]] };

    const changes = Object.fromEntries(diff) as Changes;

    onChange(changes);
  }
}

const processedClients = new WeakSet();

const planningPageQueryVariables = {
  showArchived: false,
};

const url = process.env.BINARY_URI ?? 'http://localhost:3030/';

function fetchInitialData(
  jwt: string,
  abort: AbortController,
  setInitialBinaryData: (value: BinaryPlanningPageQuery | null) => void,
  attempt = 0,
) {
  fetch(url + jwt, { signal: abort.signal })
    .then(async (r) => {
      if (!r.ok || !r.body) {
        throw new Error(`${r.status} ${r.statusText}`);
      }
      const buffer = await r.arrayBuffer();
      if (abort.signal.aborted) {
        return;
      }
      const result = parse(buffer);
      const parsed = binaryPlanningPageQuerySchema.parse(result);
      setInitialBinaryData(parsed);
    })
    .catch((reason) => {
      // Don't log errors for expected component unmount aborts
      if (reason !== 'unmount' && !abort.signal.aborted) {
        console.error(`Binary fetching failed: ${reason}`);
      }
      if (attempt < 2 && !abort.signal.aborted) {
        fetchInitialData(jwt, abort, setInitialBinaryData, attempt + 1);
      }
    });
}

export function useFetchInitialData(
  locationsById: Map<LocationId, ScenarioLocation>,
  client: ApolloClient<object>,
  useCppBackend: boolean,
) {
  const [initialBinaryData, setInitialBinaryData] =
    useState<BinaryPlanningPageQuery | null>(null);

  useEffect(() => {
    if (!useCppBackend) {
      return;
    }

    const jwt = getToken();
    if (!jwt) {
      return;
    }

    const abort = new AbortController();
    fetchInitialData(jwt, abort, setInitialBinaryData);
    return () => abort.abort('unmount');
  }, [useCppBackend]);

  const data = useMemo(
    () =>
      initialBinaryData &&
      locationsById?.size &&
      prepareInitialPlanningPageData(initialBinaryData, locationsById),
    [initialBinaryData, locationsById],
  );

  useEffect(() => {
    if (data && !processedClients.has(client)) {
      client.writeQuery<PlanningPageQuery>({
        query: PlanningPageDocument,
        variables: planningPageQueryVariables,
        data,
      });
      processedClients.add(client);
    }
  }, [data, client]);

  return initialBinaryData;
}

export type EstimateTimelineData = {
  estimate: EstimateTimeline;
  timelines: EventTimeline[];
  eventsByVessel: VesselToSortedEvents;
  scenarioOrderById: Map<OrderId, ScenarioOrderWithShipmentId>;
  tcTimeDataByVessel: Map<Ships['id'], TcTimeDataGroup>;
  scenarioPortCallByClientId: Map<ClientId, PortCallEntry>;
};

export type Expect<T extends true> = T;
export type isTypeWithoutKeys<T> = keyof T extends never ? true : false;

/**
 * For each vessel with channel incompatibilities, checks if any routes use banned channels
 * and adds routes with avoidsChannels=true.
 */
export function enrichLocationPairsForBannedChannels(
  locationPairs: readonly LocationPair[],
  eventsByVessel: VesselToSortedEvents,
  vesselsById: Map<number, Vessel>,
  distances: Distances,
): LocationPair[] {
  const enrichedLocationPairs = [...locationPairs];

  const existingAlternatives = new Set<string>();
  for (const [
    fromId,
    toId,
    avoidsChannels,
    draft,
    noGoZones,
  ] of locationPairs) {
    if (avoidsChannels) {
      existingAlternatives.add(`${fromId}-${toId}-${draft}-${noGoZones}`);
    }
  }

  eventsByVessel.forEach((_events, vesselId) => {
    const vessel = vesselsById.get(vesselId);
    if (!vessel) return;

    const bannedChannels = getVesselBannedChannels(vessel);
    if (bannedChannels.size === 0) return;

    for (const [
      fromId,
      toId,
      avoidsChannels,
      draft,
      noGoZones,
    ] of locationPairs) {
      if (!avoidsChannels) {
        const routeKey = `${fromId}-${toId}-${draft}-${noGoZones}`;
        if (existingAlternatives.has(routeKey)) {
          continue;
        }

        const routeWithChannels = distances.get(fromId, toId, false);
        const bannedChannelsInRoute = (
          routeWithChannels?.used_channels ?? []
        ).filter((c) => bannedChannels.has(c.toLowerCase()));

        if (bannedChannelsInRoute.length > 0) {
          enrichedLocationPairs.push([fromId, toId, true, draft, noGoZones]);
          existingAlternatives.add(routeKey);
        }
      }
    }
  });

  return enrichedLocationPairs;
}

export const useComputedScenarioTimings = ({
  autoUpdateUserScenarios = false,
  ignoreShipownerTimelines = false,
  onlyFetchCommittedData = false,
  skipLocalScenarios = false,
  isShipOwner = false,
  useDbValues = false,
}: ScenarioTimingInputs = defaultInputs): ComputedScenarioTimings => {
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id ?? null;
  const currentUserOrg = currentUser?.organizationId ?? null;
  const configUseCalculatedLimit =
    currentUser?.rootOrganization?.configUseCalculatedLimit ||
    (typeof window !== 'undefined' &&
      window.location.search.includes('calculated_limit'));
  const ignoreIntegrationCargoMargins =
    !!currentUser?.organization?.configIgnoreIntegrationCargoMargins;
  const portCostPriority = useMemo(() => {
    if (!currentUser?.rootOrganization?.portCostPriority) {
      return undefined;
    }

    const parsedPortCostPriority = portCostPrioritySchema.safeParse(
      JSON.parse(currentUser.rootOrganization.portCostPriority),
    );
    if (parsedPortCostPriority.success) {
      return parsedPortCostPriority.data;
    }
    return undefined;
  }, [currentUser?.rootOrganization?.portCostPriority]);

  const portCommodityLdRates = useMemo(
    () =>
      transformPortCommodityLdRates(
        currentUser?.organization?.portCommodityLdRatesByOrganizationId?.nodes,
      ),
    [currentUser?.organization?.portCommodityLdRatesByOrganizationId?.nodes],
  );

  const orgDefaultBunkerGradeIdsByArea = useMemo(
    () =>
      transformAreaBunkerGradeMappings(
        currentUser?.organization
          ?.organizationAreaBunkerGradeMappingsByOrganizationId?.nodes,
      ),
    [
      currentUser?.organization
        ?.organizationAreaBunkerGradeMappingsByOrganizationId?.nodes,
    ],
  );

  const [showArchived, setShowArchived] = useState<boolean>(false);

  const { data: locationData } = useAllLocations();
  const locations = useDefined(
    locationData?.allLocations?.nodes as ScenarioLocation[],
  );
  const locationsById = useMemo(() => mapKeyBy(locations, getId), [locations]);

  const client = useApolloClient();

  const useCppBackend = currentUser?.jwt?.role === 'root_user';
  const initialBinaryData = useFetchInitialData(
    locationsById,
    client,
    useCppBackend,
  );
  const loadingCpp = useCppBackend && !initialBinaryData;
  const [cacheOnly, setCacheOnly] = useState(useCppBackend);

  const usePlanningPageQueryResult = usePlanningPageQuery({
    variables: {
      showArchived: showArchived ? undefined : false,
    },
    fetchPolicy: cacheOnly ? 'cache-only' : 'cache-first',
  });

  const {
    data,
    loading: loadingPlanningPageQuery,
    refetch: refetchPlanningPageQuery,
  } = usePlanningPageQueryResult;

  useEffect(() => {
    if (cacheOnly && useCppBackend && data) {
      // After cpp backend is loaded, we need to switch to cache-only
      // This ensures that 'include archived orders' in operations page works
      setCacheOnly(false);
    }
  }, [useCppBackend, data, cacheOnly]);

  const loading = loadingPlanningPageQuery || loadingCpp;

  const { distances, noGoZones, queueDistancesForLocationPairs, setLocations } =
    useDistances();
  const { queuePortCostsForPairs } = useMarcuraPortCosts();

  useEffect(() => {
    setLocations(locationsById);
  }, [locationsById, setLocations]);

  const [completedLimit, setCompletedLimitActual] = useState(defaultLimit);
  const setCompletedLimit = useCallback((value: Date | null) => {
    setCompletedLimitActual(value ?? startOfLastWeek);
    defaultLimit = value ? new Date(value) : startOfLastWeek;
    if (value) {
      localStorage.setItem('completedLimit', value.toISOString());
    } else {
      localStorage.removeItem('completedLimit');
    }
  }, []);
  const completedLimitObj = useMemo(
    () => ({
      completedLimit: configUseCalculatedLimit ? +completedLimit : -Infinity,
    }),
    [completedLimit, configUseCalculatedLimit],
  );
  const committedData = useMemo(
    function (): CommittedPlanningData {
      return data !== undefined && currentUser
        ? parsePlanningData(data, currentUser, completedLimitObj)
        : initialCommittedData;
    },
    [data, currentUser, completedLimitObj],
  );
  const {
    completedOrderIds,
    completedOffHireIds,
    completedSingleEventIds,
    committedOrderById,
    portCallFragmentsByDbId,
    shipmentByClientId,
    handledBySpots,
    handledByCoas,
    singleEvents,
    offHires,
    scheduleData,
    allScheduleData,
    completedEntries,
    portCallsByClientId,
    portCallEntriesByClientId,
    committedPortIds,
    vessels,
    vesselsById,
    coas,
    organizationsById,
  } = committedData;

  const bunkerEx = useBunkerEx();
  const currencyRates = useCurrencyRates();
  const defaultCurrency = useDefaultCurrency();
  const useRoundedEstimations = hasFeature('use_rounded_estimations');
  const vesselAttributeById = useVesselAttributes(onlyFetchCommittedData);
  const voyageAttributeById = useVoyageAttributes(onlyFetchCommittedData);
  const assortmentExclusions = useAssortmentExclusions(onlyFetchCommittedData);

  const bunkerPriceByGradeAndPortCode = useMemo(
    () => parseBunkerEx(bunkerEx),
    [bunkerEx],
  );

  const bunkerGradeCalculations = useMemo(
    () => parseBunkerGradeCalculations(currentUser?.organization),
    [currentUser?.organization],
  );

  const { resolvedBunkerGrades } = useBunkerPrices();

  const { carbonPrice } = useCarbonPrice();

  /**
   * compute the calculation config once and memoize it
   */
  const calculationConfig = useMemo<CalculationConfig>(
    () => ({
      assortmentExclusions,
      defaultCurrency,
      bunkerPriceByGradeAndPortCode,
      currencyRates,
      locationsById,
      useRoundedEstimations,
      vesselAttributeById,
      voyageAttributeById,
      carbonPrice,
      portCommodityLdRates,
      orgDefaultBunkerGradeIdsByArea,
      portCostPriority,
      bunkerGradeCalculations,
      resolvedBunkerGrades,
    }),
    [
      assortmentExclusions,
      bunkerPriceByGradeAndPortCode,
      currencyRates,
      defaultCurrency,
      locationsById,
      useRoundedEstimations,
      vesselAttributeById,
      voyageAttributeById,
      carbonPrice,
      portCommodityLdRates,
      orgDefaultBunkerGradeIdsByArea,
      portCostPriority,
      bunkerGradeCalculations,
      resolvedBunkerGrades,
    ],
  );

  const portCostEstimateMaps = usePortCostEstimates(data);

  const collapsedTimelines = useState(new Set<EventTimeline['id']>());
  const collapsedTimelineIds = collapsedTimelines[0];

  const scheduleContext: ScheduleDataContext = useMemo(
    () => ({
      coasById: mapKeyBy(coas, (coa) => coa.id),
      distances: distances,
      locations: locationsById,
      portCalls: portCallsByClientId,
      vessels: vesselsById,
      portCostEstimateMaps: portCostEstimateMaps,
      currentUser,
      voyageExclusions: assortmentExclusions.voyageExclusions,
    }),
    [
      coas,
      distances,
      locationsById,
      portCallsByClientId,
      portCostEstimateMaps,
      vesselsById,
      currentUser,
      assortmentExclusions.voyageExclusions,
    ],
  );

  const completedData: CompletedData = useMemo(() => {
    if (scheduleContext.locations.size === 0) return { timelines: [] };
    const scenarioOrders = getScenarioOrdersAndShipments(
      committedOrderById,
      shipmentByClientId,
      null,
      { ignoreIntegrationCargoMargins },
    ).scenarioOrderById;

    const eventsByVessel = GroupByVesselSortByTime(
      completedEntries,
      scenarioOrders,
      scheduleContext,
      portCallEntriesByClientId,
    );

    const tcTimeDataByVessel: TcTimeDataByVessel = new Map();

    const now = Date.now();
    eventsByVessel.forEach((events, vesselId) => {
      const vessel = vesselsById.get(vesselId);

      if (!vessel) return;

      const result = DeriveCapacitiesAndTimes(
        vessel,
        events,
        scheduleContext,
        calculationConfig,
        {
          useDbValues: true,
          calculateCosts: true,
          skipSwapComputation: true,
        },
        now,
      );

      tcTimeDataByVessel.set(vessel.id, result);
    });

    const timelines = getTimelines(
      tcTimeDataByVessel,
      [],
      [],
      [],
      scheduleContext,
      currentUserOrg,
      organizationsById,
      collapsedTimelineIds,
      ignoreShipownerTimelines,
      isShipOwner,
      'scenario',
    );

    return { timelines };
  }, [
    scheduleContext,
    committedOrderById,
    shipmentByClientId,
    ignoreIntegrationCargoMargins,
    completedEntries,
    portCallEntriesByClientId,
    currentUserOrg,
    organizationsById,
    collapsedTimelineIds,
    ignoreShipownerTimelines,
    isShipOwner,
    vesselsById,
    calculationConfig,
  ]);

  /** Not pure, triggers queueDistancesForLocationPairs */
  const getScenarioData = useCallback(
    function getScenarioData<T extends ScenarioSchedule>(
      scenario: T,
    ): ScenarioData<T> {
      const scenarioPortCallByClientId: Map<ClientId, PortCallEntry> =
        mapKeyByM(scenario?.portCalls ?? emptyArr, getCId);

      const { scenarioOrderById } = getScenarioOrdersAndShipments(
        committedOrderById,
        shipmentByClientId,
        scenario,
        { ignoreIntegrationCargoMargins },
      );

      const spotEntries: readonly SpotEntry[] = scenario?.spotAssignments ?? [];
      const coaEntries: readonly CoaEntry[] = scenario?.coaAssignments ?? [];

      const eventsByVessel = GroupByVesselSortByTime(
        scenario?.schedule ?? [],
        scenarioOrderById,
        scheduleContext,
        scenarioPortCallByClientId,
      );

      const coaEventGroups = groupCoaEntriesToSequences(
        coaEntries,
        scenarioOrderById,
        scheduleContext,
        scenarioPortCallByClientId,
      );

      const spotEventGroups = groupSpotEntriesToSequences(
        spotEntries,
        scenarioOrderById,
        scheduleContext,
        scenarioPortCallByClientId,
      );

      // Some distances might have not been fetched if a scenario being compared
      // hasn't been activated previously, so we need to trigger distance fetching
      // for any missing ports here
      const allEventGroups: Events[][] = [
        ...eventsByVessel.values(),
        ...coaEventGroups.map((e) => e.events),
        ...spotEventGroups.map((e) => e.events),
      ];
      const locationPairs = getResolvedLocationPairsFromEvents(
        allEventGroups,
        undefined,
        noGoZones,
        scheduleContext.locations,
      );

      const enrichedLocationPairs = enrichLocationPairsForBannedChannels(
        locationPairs,
        eventsByVessel,
        vesselsById,
        scheduleContext.distances,
      );

      setTimeout(() => {
        // In timeout so we don't call a reducer while already rendering (getScenarioData is called)
        queueDistancesForLocationPairs(enrichedLocationPairs);
        if (hasFeature('marcura_integration')) {
          const marcuraPortCostPairs = getMarcuraPortCostPairs(allEventGroups);
          queuePortCostsForPairs(marcuraPortCostPairs);
        }
      });

      const tcTimeDataByVessel: TcTimeDataByVessel = new Map();

      const now = Date.now();
      eventsByVessel.forEach((events, vesselId) => {
        const vessel = vesselsById.get(vesselId);

        if (!vessel) return;

        const result = DeriveCapacitiesAndTimes(
          vessel,
          events,
          scheduleContext,
          calculationConfig,
          { calculateCosts: true, useDbValues },
          now,
        );

        tcTimeDataByVessel.set(vessel.id, result);
      });

      const coaTimeDataGroups = coaEventGroups.map<CoaTimeDataGroup>(
        (eventGroup) => {
          const vessel =
            eventGroup.vesselId !== null
              ? vesselsById.get(eventGroup.vesselId)
              : null;

          const result = DeriveCapacitiesAndTimes(
            vessel ?? defaultVessel,
            eventGroup.events,
            scheduleContext,
            calculationConfig,
            { calculateCosts: false, useDbValues },
            now,
          );

          return {
            key: eventGroup.key,
            coaId: eventGroup.coaId,
            vesselId: eventGroup.vesselId,
            ...result,
          };
        },
      );

      const spotTimeDataGroups = spotEventGroups.map<SpotTimeDataGroup>(
        (eventGroup) => {
          const vessel =
            eventGroup.vesselId !== null
              ? vesselsById.get(eventGroup.vesselId)
              : null;

          const result = DeriveCapacitiesAndTimes(
            vessel ?? defaultVessel,
            eventGroup.events,
            scheduleContext,
            calculationConfig,
            { calculateCosts: false, useDbValues },
            now,
          );

          return {
            key: eventGroup.key,
            shipownerOrgId: eventGroup.shipownerOrgId,
            vesselId: eventGroup.vesselId,
            ...result,
          };
        },
      );

      const timelines = getTimelines(
        tcTimeDataByVessel,
        coaTimeDataGroups,
        spotTimeDataGroups,
        coas,
        scheduleContext,
        currentUserOrg,
        organizationsById,
        collapsedTimelineIds,
        ignoreShipownerTimelines,
        isShipOwner,
        'scenario',
      );

      return {
        scenario,
        timelines,
        spotEntries,
        spotEventGroups,
        coaEntries,
        coaEventGroups,
        eventsByVessel,
        scenarioOrderById,
        tcTimeDataByVessel,
        scenarioPortCallByClientId,
      };
    },
    [
      committedOrderById,
      shipmentByClientId,
      ignoreIntegrationCargoMargins,
      scheduleContext,
      noGoZones,
      coas,
      currentUserOrg,
      organizationsById,
      collapsedTimelineIds,
      ignoreShipownerTimelines,
      isShipOwner,
      queueDistancesForLocationPairs,
      queuePortCostsForPairs,
      vesselsById,
      calculationConfig,
      useDbValues,
    ],
  );

  /** Not pure, triggers queueDistancesForLocationPairs */
  const getEstimateData = useCallback(
    function getEstimateData(estimate: EstimateTimeline): EstimateTimelineData {
      const scenarioPortCallByClientId: Map<ClientId, PortCallEntry> =
        mapKeyByM(estimate?.portCalls ?? emptyArr, getCId);

      const { scenarioOrderById } = getScenarioOrdersAndShipments(
        committedOrderById,
        shipmentByClientId,
        estimate,
        { ignoreIntegrationCargoMargins },
      );

      // Note: Freight overrides are consumed by VoyageCosts via estimate.freightRateOverrides,
      // not by mutating scenarioOrderById.

      const vessels = new Map<number, Vessel>();
      const vessel = scheduleContext.vessels.get(estimate.vesselId);
      if (vessel) {
        vessels.set(estimate.vesselId, vessel);
      }
      const context = { ...scheduleContext, vessels: vessels };

      const eventsByVessel = GroupByVesselSortByTime(
        estimate.schedule ?? [],
        scenarioOrderById,
        context,
        scenarioPortCallByClientId,
      );

      // Some distances might have not been fetched if a scenario being compared
      // hasn't been activated previously, so we need to trigger distance fetching
      // for any missing ports here
      const allEventGroups: Events[][] = [...eventsByVessel.values()];
      const locationPairs = getResolvedLocationPairsFromEvents(
        allEventGroups,
        undefined,
        noGoZones,
        scheduleContext.locations,
      );

      const enrichedLocationPairs = enrichLocationPairsForBannedChannels(
        locationPairs,
        eventsByVessel,
        vesselsById,
        scheduleContext.distances,
      );

      setTimeout(() => {
        // In timeout so we don't call a reducer while already rendering (getScenarioData is called)
        queueDistancesForLocationPairs(enrichedLocationPairs);
        if (hasFeature('marcura_integration')) {
          const marcuraPortCostPairs = getMarcuraPortCostPairs(allEventGroups);
          queuePortCostsForPairs(marcuraPortCostPairs);
        }
      });

      const tcTimeDataByVessel: TcTimeDataByVessel = new Map();

      const now = Date.now();
      eventsByVessel.forEach((events, vesselId) => {
        const vessel = vesselsById.get(vesselId);

        if (!vessel) return;

        const result = DeriveCapacitiesAndTimes(
          vessel,
          events,
          context,
          calculationConfig,
          { calculateCosts: true, useDbValues },
          now,
        );

        tcTimeDataByVessel.set(vessel.id, result);
      });

      const timelines = getTimelines(
        tcTimeDataByVessel,
        [],
        [],
        [],
        context,
        currentUserOrg,
        organizationsById,
        collapsedTimelineIds,
        ignoreShipownerTimelines,
        isShipOwner,
        estimate.clientId,
      );

      return {
        estimate,
        timelines,
        eventsByVessel,
        scenarioOrderById,
        tcTimeDataByVessel,
        scenarioPortCallByClientId,
      };
    },
    [
      calculationConfig,
      collapsedTimelineIds,
      committedOrderById,
      currentUserOrg,
      ignoreIntegrationCargoMargins,
      ignoreShipownerTimelines,
      isShipOwner,
      noGoZones,
      organizationsById,
      queueDistancesForLocationPairs,
      queuePortCostsForPairs,
      scheduleContext,
      shipmentByClientId,
      useDbValues,
      vesselsById,
    ],
  );

  const committedAt = data?.lastCommit?.nodes[0]?.committedAt;
  const masterCommitAt = dateStringToNumOpt(committedAt);
  const isCommitRunning = useRef(false);

  const useScenariosResult = useScenarios(
    handledBySpots,
    committedOrderById,
    scheduleData,
    allScheduleData,
    handledByCoas,
    portCallFragmentsByDbId,
    shipmentByClientId,
    loading,
    skipLocalScenarios,
    {
      autoUpdateUserScenarios,
      masterCommitAt,
    },
    isCommitRunning,
    onFinishCommit,
    completedOrderIds,
    completedOffHireIds,
    completedSingleEventIds,
    onlyFetchCommittedData,
  );

  /**
   * Non-auto-sync scenarios aren't refreshed by cache eviction, so we
   * explicitly remove archived items and recompute derived timing state.
   */
  const removeArchivedFromScenarios = useEvent(
    (archivedIds: {
      orderIds: OrderId[];
      singleEventIds: SingleEventId[];
      offHireIds: OffHireId[];
      portCallIds: PortCallId[];
    }) => {
      const singleEventsById = mapKeyBy(singleEvents, (e) => e.id);
      const offHiresById = mapKeyBy(offHires, (e) => e.id);

      const archivedState: ArchivedState = {
        archivedItems: {
          orders: new Set(archivedIds.orderIds),
          [EventCategory.Single]: new Set(
            archivedIds.singleEventIds
              .map((id) => singleEventsById.get(id)?.clientId)
              .filter(isDefined)
              .map(parseClientId),
          ),
          [EventCategory.OffHire]: new Set(
            archivedIds.offHireIds
              .map((id) => offHiresById.get(id)?.clientId)
              .filter(isDefined)
              .map(parseClientId),
          ),
          [EventCategory.PortCall]: new Set(
            archivedIds.portCallIds
              .map((id) => portCallFragmentsByDbId.get(id)?.clientId)
              .filter(isDefined)
              .map(parseClientId),
          ),
        },
      };

      useScenariosResult.setScenarios((oldScenarios) =>
        oldScenarios.map((s) => {
          const stripped = updateScenarioFromArchivedState(s, archivedState);

          // Cascade timing changes caused by the removal
          if (isUserScenario(stripped) || isSuggestionScenario(stripped)) {
            const { timelines } = getScenarioData(stripped);
            return updateScenarioFromTimelines(stripped, timelines);
          }

          return stripped;
        }),
      );
    },
  );

  const hasLocations = locations.length !== 0;

  const masterScenario: MasterSchedule | undefined = useMemo(
    () => useScenariosResult.scenarios.find(isMasterScenario),
    [useScenariosResult.scenarios],
  );
  const masterInput: MasterSchedule | InternalInitialSchedule =
    masterScenario && hasLocations ? masterScenario : dummyScenario;
  const masterData: ScenarioData<MasterSchedule | InternalInitialSchedule> =
    useMemo(
      () =>
        masterInput !== dummyScenario
          ? getScenarioData(masterInput)
          : dummyScenarioData,
      [getScenarioData, masterInput],
    );
  useWdyc(masterData, { getScenarioData, masterInput }, (changes) => {
    console.log(changes);
  });

  const {
    estimateTimelines,
    shared,
    clientId,
    cargoAllocations,
    updatedAt,
    autoUpdateFromMaster,
    type,
    updatedBy,
    isIntegrationMaster,
    masterCommitAt: scenarioMasterCommitAt,
    integrationData,
    name,
    dataSchemaVersion,
    coaAssignments,
    createdBy,
    createdAt,
    spotAssignments,
    deletedAt,
    description,
    importedAt,
    initials,
    isReadOnly,
    organizationId,
    orderShortlist,
    patchOrders,
    patchShipments,
    vesselsShortlist,
    voyageCharterByOrderId,
    schedule,
    portCalls,
    baseScenarioId,
    cleanSlateLayer,
    backedUpAt,
    id,
    entryStates,
    migrationVersion,
    syncedWithMasterAt,
    changedAt,
    freightByShipmentClientId,
    extraFreightByShipmentClientId,
    ..._rest
  } = useScenariosResult.selectedScenario as Omit<
    InternalUserSchedule,
    'type'
  > &
    Omit<InternalSuggestionSchedule, 'type'> &
    Omit<InternalMasterSchedule, 'type'> &
    Omit<InternalBackupSchedule, 'type'> &
    Omit<InternalInitialSchedule, 'type'> & {
      type:
        | InternalUserSchedule['type']
        | InternalSuggestionSchedule['type']
        | InternalMasterSchedule['type']
        | InternalBackupSchedule['type']
        | InternalInitialSchedule['type'];
    };
  if (false) {
    // @ts-expect-no-error rest has no keys
    type _CheckTags = Expect<isTypeWithoutKeys<typeof _rest>>;
  }

  // Exclude estimateTimelines and changedAt to improve memoization
  const selectedScenario = useMemo(
    () => ({
      estimateTimelines: undefined,
      shared,
      clientId,
      cargoAllocations,
      updatedAt,
      autoUpdateFromMaster,
      type,
      updatedBy,
      isIntegrationMaster,
      masterCommitAt: scenarioMasterCommitAt,
      integrationData,
      name,
      dataSchemaVersion,
      coaAssignments,
      createdBy,
      createdAt,
      spotAssignments,
      deletedAt,
      description,
      importedAt,
      initials,
      isReadOnly,
      organizationId,
      orderShortlist,
      patchOrders,
      patchShipments,
      vesselsShortlist,
      voyageCharterByOrderId,
      schedule,
      portCalls,
      baseScenarioId,
      cleanSlateLayer,
      backedUpAt,
      id,
      entryStates,
      migrationVersion,
      syncedWithMasterAt,
      changedAt: null,
      freightByShipmentClientId,
      extraFreightByShipmentClientId,
    }),
    [
      shared,
      clientId,
      cargoAllocations,
      updatedAt,
      autoUpdateFromMaster,
      type,
      updatedBy,
      isIntegrationMaster,
      scenarioMasterCommitAt,
      integrationData,
      name,
      dataSchemaVersion,
      coaAssignments,
      createdBy,
      createdAt,
      spotAssignments,
      deletedAt,
      description,
      importedAt,
      initials,
      isReadOnly,
      organizationId,
      orderShortlist,
      patchOrders,
      patchShipments,
      vesselsShortlist,
      voyageCharterByOrderId,
      schedule,
      portCalls,
      baseScenarioId,
      cleanSlateLayer,
      backedUpAt,
      id,
      entryStates,
      migrationVersion,
      syncedWithMasterAt,
      freightByShipmentClientId,
      extraFreightByShipmentClientId,
    ],
  );
  const selectedScenarioInput:
    | (ScenarioSchedule & {
        estimateTimelines: undefined;
      })
    | InternalInitialSchedule = hasLocations ? selectedScenario : dummyScenario;
  const selectedScenarioData: ScenarioData<
    | (ScenarioSchedule & {
        estimateTimelines: undefined;
      })
    | InternalInitialSchedule
  > = useMemo(
    () =>
      selectedScenarioInput !== dummyScenario
        ? getScenarioData(selectedScenarioInput)
        : dummyScenarioData,
    [getScenarioData, selectedScenarioInput],
  );

  const estimateTimelinesData = useMemo(
    () => estimateTimelines?.map((estimate) => getEstimateData(estimate)),
    [getEstimateData, estimateTimelines],
  );

  usePlanningUpdates(currentUserOrg);

  const { data: usersData } = useUsersQuery();
  const userById = useMemo(
    () => mapKeyBy(compact(usersData?.allUsers?.nodes), getId),
    [usersData?.allUsers?.nodes],
  );

  const commitLogOptions = useMemo(() => {
    const commitLogOptions: Parameters<
      typeof useCommitLogUpdatedSubscription
    >[0] = {
      variables: {
        org: `${currentUserOrg}`,
      },
      skip: currentUserOrg == null,
      onData: async (options) => {
        const { data, client } = options;
        const commitLog = data.data?.commitLogUpdated?.commitLog;
        if (!commitLog) return;
        if (isCommitRunning.current) {
          queuedCommitLogEntries.push([
            Date.parse(commitLog.committedAt),
            () => {
              applyCommitLogEntry(
                commitLog,
                assertedDefined(currentUserId, 'currentUserId'),
                client,
                locationsById,
                userById,
              );
            },
          ]);
        } else {
          applyCommitLogEntry(
            commitLog,
            assertedDefined(currentUserId, 'currentUserId'),
            client,
            locationsById,
            userById,
          );
        }
      },
    };
    return commitLogOptions;
  }, [currentUserId, currentUserOrg, locationsById, userById]);
  useCommitLogUpdatedSubscription(commitLogOptions);

  const reconnectHandler = useEvent(async () => {
    const reconnect = await client.query<ReconnectQuery>({
      query: ReconnectDocument,
      fetchPolicy: 'network-only',
    });
    const cIds: string[] = [];
    for (const scenario of reconnect.data?.allScenarios?.nodes ?? []) {
      if (!scenario) {
        continue;
      }
      const { clientId, updatedAt } = scenario;
      const cid = parseClientId(clientId);
      const existing = useScenariosResult.scenarios.find(
        (s) => s.clientId === cid,
      );
      const existingUpdated = existing?.updatedAt;
      if (
        updatedAt &&
        (!existingUpdated ||
          Date.parse(existingUpdated) < Date.parse(updatedAt))
      ) {
        cIds.push(clientId);
      }
    }
    if (cIds.length) {
      client
        .query<ScenariosByIdsQuery, ScenariosByIdsQueryVariables>({
          query: ScenariosByIdsDocument,
          variables: {
            scenarioDataSchemaVersion: SCENARIO_DATA_SCHEMA_VERSION,
            cids: cIds,
          },
          fetchPolicy: 'network-only',
        })
        .then((value) => {
          const scenarios = value.data.allScenarios?.nodes;
          if (scenarios) {
            useScenariosResult.updateScenariosOnReconnect(scenarios);
          }
        });
    }
    const result = await client.query<
      CommitsSinceDisconnectQuery,
      CommitsSinceDisconnectQueryVariables
    >({
      query: CommitsSinceDisconnectDocument,
      variables: {
        committedAt: committedAt ?? new Date(0).toISOString(),
      },
      fetchPolicy: 'network-only',
    });
    for (const commitLog of result.data.allCommitLogs?.nodes ?? []) {
      if (!commitLog) {
        continue;
      }
      applyCommitLogEntry(
        commitLog,
        assertedDefined(currentUserId, 'currentUserId'),
        client,
        locationsById,
        userById,
      );
    }
  });
  useEffect(() => onReconnect(reconnectHandler), [reconnectHandler]);

  const userNotificationSettings =
    currentUser?.userSettingsByUserId?.nodes.find(
      (s) => s?.userId === currentUserId,
    );

  const batchUpdateOptions = useMemo<
    ApolloReactHooks.SubscriptionHookOptions<
      BatchUpdateSubscription,
      BatchUpdateSubscriptionVariables
    > & { skip: boolean }
  >(
    () => ({
      variables: {
        org: `${currentUserOrg}`,
      },
      skip: !currentUserOrg,
      fetchPolicy: 'no-cache' as const,
      async onData(options) {
        const { data, client } = options;
        const jsonString = data.data?.batchUpdate?.batchUpdate?.data;
        if (typeof jsonString !== 'string') {
          return;
        }
        const json: BatchData = JSON.parse(jsonString);
        if ('archive' in json) {
          const {
            orderIds,
            scheduledOrderIds,
            handledByCoaIds,
            handledBySpotIds,
            offHireIds,
            singleEventIds,
            portCallIds,
            archived,
            ...rest
          } = json.archive;
          rest satisfies EmptyObject; // Ensures no other properties are present
          if (archived) {
            const { cache } = client;
            const evict = (
              typename: string,
              ids: number[] | null | undefined,
            ) => {
              for (const id of ids ?? []) {
                cache.evict({
                  id: cache.identify({ __typename: typename, id }),
                });
              }
            };
            cache.batch({
              update() {
                evict('Order', orderIds);
                evict('ScheduledOrder', scheduledOrderIds);
                evict('HandledByCoa', handledByCoaIds);
                evict('HandledBySpot', handledBySpotIds);
                evict('OffHire', offHireIds);
                evict('SingleEvent', singleEventIds);
                evict('PortCall', portCallIds);
              },
            });
            cache.gc();
            // Strip archived items from all scenario states (including non-auto-sync ones)
            removeArchivedFromScenarios({
              orderIds: (orderIds as OrderId[]) ?? [],
              singleEventIds: (singleEventIds as SingleEventId[]) ?? [],
              offHireIds: (offHireIds as OffHireId[]) ?? [],
              portCallIds: (portCallIds as PortCallId[]) ?? [],
            });
          } else {
            // unarchive should be rare enough that we can use
            // refetchQueries for all queries
            client.refetchQueries({ include: 'all' });
          }
          return;
        }
        const fetchPorts = Object.keys(json.createdPortIdByName).length > 0;
        const fetchShips = Object.keys(json.createdVesselIdByName).length > 0;
        const fetchOrganizations =
          Object.keys(json.createdOrganizationIdByName).length > 0;
        const fetchAssortments =
          Object.keys(json.createdAssortmentIdByName).length > 0;

        const fetchUpdated = json.updatedOrderIds.length > 0;
        const fetchInserted = json.insertedOrderIds.length > 0;
        const fetchUpdatedCoas = (json.updatedCoaIds ?? []).length > 0;
        const fetchInsertedCoas = (json.insertedCoaIds ?? []).length > 0;
        const fetchUpdatedVcs = (json.updatedVoyageCharterIds ?? []).length > 0;

        const fetchOrders = fetchUpdated || fetchInserted;

        if (
          fetchOrganizations ||
          fetchShips ||
          fetchOrders ||
          fetchAssortments ||
          fetchUpdatedCoas ||
          fetchInsertedCoas ||
          fetchUpdatedVcs
        ) {
          const result = await client.query<BatchDataQuery>({
            query: BatchDataDocument,
            fetchPolicy: 'network-only',
            variables: {
              updatedIds: json.updatedOrderIds,
              insertedIds: json.insertedOrderIds,
              updatedCoaIds: json.updatedCoaIds ?? [],
              insertedCoaIds: json.insertedCoaIds ?? [],
              updatedVcIds: json.updatedVoyageCharterIds ?? [],
              includeOrgs: fetchOrganizations,
              includeShips: fetchShips,
              includeAssortments: fetchAssortments,
              includeUpdated: fetchUpdated,
              includeInserted: fetchInserted,
              includeUpdatedCoas: fetchUpdatedCoas,
              includeInsertedCoas: fetchInsertedCoas,
              includeUpdatedVcs: fetchUpdatedVcs,
            },
          });

          if (
            fetchOrders &&
            json.source === 'integration' &&
            userNotificationSettings
          ) {
            const allOrders = [
              ...(result.data?.inserted?.nodes ?? []),
              ...(result.data?.updated?.nodes ?? []),
            ];
            const hasOwnOrders = allOrders.some(
              (o) => o?.userByUserId?.id === currentUserId,
            );

            const isEnabled = hasOwnOrders
              ? (userNotificationSettings.notificationYourOrders
                  ?.ordersUpsertedViaIntegration ??
                userNotificationSettings.notificationAllOrders
                  ?.ordersUpsertedViaIntegration)
              : userNotificationSettings.notificationAllOrders
                  ?.ordersUpsertedViaIntegration;

            if (isEnabled) {
              const insertedOrders = (result.data?.inserted?.nodes ?? [])
                .filter(Boolean)
                .map(mapToOrderSummary);
              const updatedOrders = (result.data?.updated?.nodes ?? [])
                .filter(Boolean)
                .map(mapToOrderSummary);
              if (insertedOrders.length > 0 || updatedOrders.length > 0) {
                toast.info(
                  createIntegrationOrdersToast(insertedOrders, updatedOrders),
                  { autoClose: false },
                );
              }
            }
          }

          const inserted = result.data?.inserted?.nodes ?? [];
          if (inserted.length) {
            const dataInStore = client.readQuery<AllOrdersQuery>({
              query: AllOrdersDocument,
            });
            const nodes = dataInStore?.allOrders?.nodes;
            if (nodes) {
              const next = {
                ...dataInStore,
                allOrders: {
                  ...dataInStore.allOrders,
                  nodes: [...nodes, ...inserted],
                },
              };
              client.writeQuery<AllOrdersQuery>({
                query: AllOrdersDocument,
                data: next,
              });
            }
          }

          const insertedCoas = result.data?.insertedCoas?.nodes ?? [];
          if (insertedCoas.length) {
            const dataInStore = client.readQuery<PlanningPageQuery>({
              query: PlanningPageDocument,
              variables: planningPageQueryVariables,
            });
            if (dataInStore?.allCoaContracts) {
              client.writeQuery<PlanningPageQuery>({
                query: PlanningPageDocument,
                variables: planningPageQueryVariables,
                data: {
                  ...dataInStore,
                  allCoaContracts: {
                    ...dataInStore.allCoaContracts,
                    nodes: [
                      ...(dataInStore.allCoaContracts.nodes ?? []),
                      ...insertedCoas,
                    ],
                  },
                },
              });
            }
          }
        }

        if (fetchPorts) {
          await client.refetchQueries({ include: ['AllLocations'] });
        }
      },
    }),
    [
      currentUserOrg,
      currentUserId,
      userNotificationSettings,
      removeArchivedFromScenarios,
    ],
  );
  useBatchUpdateSubscription(batchUpdateOptions);

  const dbChangeOptions: Parameters<typeof useDbChangeSubscription>[0] =
    useMemo(
      () => ({
        variables: {
          org: `${currentUserOrg}`,
        },
        skip: !currentUserOrg,
        fetchPolicy: 'no-cache' as const,
        async onData(options) {
          const { data, client } = options;
          const dbChange = data.data?.dbChange?.dbChange;
          if (!dbChange) {
            return;
          }
          const {
            /*
          deletedBillOfLadingData,
          deletedCargoCompartmentAllocation,
          deletedCargoExclusion,
          deletedCargoMeasurement,
          deletedNotificationReceivers,
          deletedOrderPortCallRelation,
          deletedOrderVoyageCharterRelation,
          */
            softDeletedOrders,
          } = dbChange;
          const dataInStore = client.readQuery<AllOrdersQuery>({
            query: AllOrdersDocument,
          });
          if (!dataInStore) {
            return;
          }
          const allOrders = dataInStore.allOrders;
          if (!allOrders) {
            return;
          }
          const nodes = allOrders.nodes;
          if (!nodes) {
            return;
          }
          const softDeletedOrderIds = new Set(softDeletedOrders);
          const next = {
            ...dataInStore,
            allOrders: {
              ...allOrders,
              nodes: nodes.filter((o) => o && !softDeletedOrderIds.has(o.id)),
            },
          };
          client.writeQuery<AllOrdersQuery>({
            query: AllOrdersDocument,
            data: next,
          });
        },
      }),
      [currentUserOrg],
    );
  useDbChangeSubscription(dbChangeOptions);

  const [scenarioPortIds, setScenarioPortIds] = useState<Set<LocationId>>(
    new Set(),
  );
  const portIds = useMemo(
    () => new Set([...committedPortIds, ...scenarioPortIds]),
    [committedPortIds, scenarioPortIds],
  );

  // Supplement port ids present in all scenarios so that their distance data can be loaded
  useEffect(() => {
    const ids = new Set<LocationId>(scenarioPortIds);

    for (const vessel of vessels) {
      for (const tcContract of vessel.tcContractsByVesselId.nodes) {
        if (!tcContract) continue;
        ids.add(tcContract.deliveryPortId);
        ids.add(tcContract.redeliveryPortId);
      }
    }

    useScenariosResult.scenarios.forEach((s) =>
      s.schedule.forEach((e) => {
        if (isOffHireEntry(e)) {
          ids.add(e.locationAId);
          ids.add(e.locationBId);
        } else if (isSingleEntry(e)) {
          ids.add(e.locationId);
        } else if (isTCOrderEntry(e)) {
          const order = committedOrderById.get(e.orderId);
          if (!order) {
            return;
          }
          const { loadPortId, dischargePortId } = order;
          if (typeof loadPortId === 'number') {
            ids.add(loadPortId);
          }
          ids.add(dischargePortId);
        } else {
          exhaustiveCheck(e);
        }
      }),
    );
    if (ids.size > scenarioPortIds.size) {
      setScenarioPortIds(ids);
    }
  }, [
    scenarioPortIds,
    useScenariosResult.scenarios,
    committedOrderById,
    vessels,
  ]);

  return {
    masterData,
    selectedScenarioData,
    estimateTimelinesData,
    vessels,
    handledByCoas,
    singleEvents,
    offHires,
    useScenariosResult,
    calculationConfig,
    coas,
    data,
    committedData,
    distances,
    noGoZones,
    organizationsById,
    portIds,
    committedOrderById,
    portCostEstimateMaps,
    getScenarioData,
    loading,
    showArchived,
    setShowArchived,
    refetchPlanningPageQuery,
    scheduleContext,
    collapsedTimelines,
    completedData,
    completedLimit,
    setCompletedLimit,
    configUseCalculatedLimit,
  };
};

type SingleEventPortCallRelation = {
  __typename?: 'SingleEventPortCallRelation';
  id: number;
  portCallId: number;
  singleEventId: number;
};

const memoCompact = memoize(<T>(arr: T[]): NonNullable<T>[] =>
  arr.filter(isDefined),
);
const emptyArr: [] = [] as const;
const compact = <T>(arr: T[] | null | undefined): Array<NonNullable<T>> =>
  arr ? memoCompact(arr) : emptyArr;

export const getId = <T extends { id: unknown }>(o: T): T['id'] => o.id;

export const getCId = <T extends { clientId: string }>(o: T): ClientId =>
  parseClientId(o.clientId);

const getDbId = <T extends { dbId: unknown }>(o: T): T['dbId'] => o.dbId;

const getOrderId = <T extends { orderId: unknown }>(o: T): T['orderId'] =>
  o.orderId;

const getVoyageCharterId = <T extends { voyageCharterId: unknown }>(
  o: T,
): T['voyageCharterId'] => o.voyageCharterId;

const getOrderVoyageCharterRelationId = <
  T extends { orderVoyageCharterRelationId: unknown },
>(
  o: T,
): T['orderVoyageCharterRelationId'] => o.orderVoyageCharterRelationId;

const getCompartmentId = <T extends { compartmentId: unknown }>(
  o: T,
): T['compartmentId'] => o.compartmentId;

const getIsLoadPort = <T extends { isLoadPort: unknown }>(
  o: T,
): T['isLoadPort'] => o.isLoadPort;

const getPortCallId = <T extends { portCallId: unknown }>(
  o: T,
): T['portCallId'] => o.portCallId;

const getSingleEventId = <T extends { singleEventId: unknown }>(
  o: T,
): T['singleEventId'] => o.singleEventId;

const getShipmentFragments = memoize(function getShipmentFragments(
  shipments: Array<ScenarioTimeline_ShipmentFragment>,
) {
  return shipments.map(shipmentFragmentToPersistedShipment);
});

const getCargoAllocations = memoize(function getCargoAllocations(
  orders: Array<ScenarioTimeline_OrderFragment>,
) {
  return orders.flatMap((order) =>
    compact(order.cargoCompartmentAllocationsByOrderId.nodes).map(
      CargoAllocationFragmentToEntry,
    ),
  );
});

const getHandledByCoaEntries = memoize2(function getHandledByCoaEntries(
  handledByCoa: Array<ScenarioTimeline_CoaAssignmentFragment>,
  committedOrderById: Map<number, ScenarioTimeline_OrderFragment>,
) {
  return handledByCoa.map((e) => coaFragmentToEntry(e, committedOrderById));
});

const getHandledBySpotEntries = memoize2(function getHandledBySpotEntries(
  handledBySpot: Array<ScenarioTimeline_HandledBySpotFragment>,
  committedOrderById: Map<number, ScenarioTimeline_OrderFragment>,
) {
  return handledBySpot.map((e) => spotFragmentToEntry(e, committedOrderById));
});

const getScheduledEntries = memoize3(function getScheduledEntries(
  scheduledOrders: Array<ScheduledOrderFragment>,
  committedOrderById: Map<number, ScenarioTimeline_OrderFragment>,
  shipmentByDbId: Map<number, PersistedShipment>,
) {
  return scheduledOrders.map((e) =>
    scheduledOrderFragmentToTCOrderEntry(e, committedOrderById, shipmentByDbId),
  );
});

const getOffHireEntries = memoize(function getOffHireEntries(
  offHires: Array<ScenarioTimeline_OffHireFragment>,
) {
  return offHires.map((e) => offHireFragmentToOffHireEntry(e));
});

const getSingleEventEntries = memoize2(function getSingleEventEntries(
  singleEvents: Array<ScenarioTimeline_SingleEventFragment>,
  portCallFragmentsByDbId: Map<
    number,
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >,
) {
  return singleEvents.map((e) =>
    singleEventFragmentToSingleEntry(e, portCallFragmentsByDbId),
  );
});

const getPortCallEntries = memoize(function getPortCallEntries(
  portCallFragments: Array<
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >,
) {
  return portCallFragments.map((e) => portCallFragmentToEntry(e));
});

const getOPCRs = memoize(function getOPCRs(
  orders: Array<ScenarioTimeline_OrderFragment>,
) {
  return orders.flatMap((x) =>
    compact(x.orderPortCallRelationsByOrderId.nodes),
  );
});

const getLocationIds = memoize3(function getLocationIds(
  orders: Array<ScenarioTimeline_OrderFragment>,
  singleEvents: Array<ScenarioTimeline_SingleEventFragment>,
  offHires: Array<ScenarioTimeline_OffHireFragment>,
) {
  const ids = new Set<LocationId>();

  for (const o of orders) {
    if (typeof o.loadPortId === 'number') {
      ids.add(o.loadPortId);
    }
    ids.add(o.dischargePortId);
  }
  for (const se of singleEvents) {
    ids.add(se.locationId);
  }
  for (const oh of offHires) {
    ids.add(oh.locationAId);
    ids.add(oh.locationBId);
  }

  return Array.from(ids);
});

const getScheduleData = memoize3(function getScheduleData(
  scheduledEntries: Array<WithDbId<Required<TCOrderEntry>, ScheduledOrderId>>,
  singleEventEntries: Array<WithDbId<Required<SingleEntry>, SingleEventId>>,
  offHireEntries: Array<WithDbId<Required<OffHireEntry>, OffHireId>>,
) {
  return [...scheduledEntries, ...singleEventEntries, ...offHireEntries];
});

export const speedConsumptionsToMaps = (
  consumptions: (VesselSpeedConsumptionFragment | null)[],
) => {
  const vscs = compact(consumptions);
  return {
    speedConsumptions: mapGroupBy2(
      vscs,
      (vsc) => vsc.type,
      (vsc) => vsc.area ?? null,
    ),
    speedConsumptionsByTag: mapGroupBy3(
      vscs,
      (vsc) => vsc.tag ?? null,
      (vsc) => vsc.type,
      (vsc) => vsc.area ?? null,
    ),
  };
};

export const draftDatapointsToMaps = (
  datapoints: (VesselDraftDatapointFragment | null)[],
) => {
  const dpts = compact(datapoints);
  return {
    draftDatapoints: dpts.map(({ cargoLoad, id, draft }) => ({
      cargoLoad,
      id,
      draft,
    })),
  };
};

const parseVessel = memoize((vessel: ScenarioTimeline_ShipFragment): Vessel => {
  return {
    ...vessel,
    ...speedConsumptionsToMaps(vessel.vesselSpeedConsumptionsByShipId.nodes),
    ...draftDatapointsToMaps(vessel.vesselDraftDatapointsByShipId.nodes),
  };
});

const getSingleEventPortCallRelations = memoize(
  function getSingleEventPortCallRelations(
    singleEvents: Array<ScenarioTimeline_SingleEventFragment>,
  ) {
    return singleEvents.flatMap((x) =>
      compact(x.singleEventPortCallRelationsBySingleEventId.nodes),
    );
  },
);

export type CommittedPlanningData = {
  completedOrderIds: Set<number>;
  completedOffHireIds: Set<number>;
  completedSingleEventIds: Set<number>;
  orders: Array<ScenarioTimeline_OrderFragment>;
  committedOrderById: Map<number, ScenarioTimeline_OrderFragment>;
  orderPortCallRelationsByOrderId: DefaultMap<
    number,
    Map<boolean, OrderPortCallRelation | null>
  >;
  cargoAllocations: Map<
    ClientId,
    WithDbId<CargoAllocation, CargoCompartmentAllocationId>
  >;
  scheduledEntries: Array<WithDbId<Required<TCOrderEntry>, ScheduledOrderId>>;
  tcOrderEntries: Map<ClientId, WithDbId<TCOrderEntry, ScheduledOrderId>>;
  completedEntries: Array<
    WithDbId<
      Required<TCOrderEntry | SingleEntry | OffHireEntry>,
      ScheduledOrderId | SingleEventId | OffHireId
    >
  >;
  completedTcOrderEntries: Map<
    ClientId,
    WithDbId<TCOrderEntry, ScheduledOrderId>
  >;
  handledByCoas: Array<ScenarioTimeline_CoaAssignmentFragment>;
  coaEntries: Map<ClientId, WithDbId<CoaEntry, HandledByCoaId>>;
  handledBySpots: Array<ScenarioTimeline_HandledBySpotFragment>;
  spotEntries: Map<ClientId, WithDbId<SpotEntry, HandledBySpotId>>;
  offHires: Array<ScenarioTimeline_OffHireFragment>;
  offHireEntries: Array<WithDbId<Required<OffHireEntry>, OffHireId>>;
  offHireEntriesByClientId: Map<ClientId, WithDbId<OffHireEntry, OffHireId>>;
  portCallFragments: Array<
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >;
  portCallFragmentsByDbId: Map<
    number,
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >;
  portCallEntriesByClientId: Map<ClientId, WithDbId<PortCallEntry, PortCallId>>;
  portCallEntriesByDbId: Map<number, WithDbId<PortCallEntry, PortCallId>>;
  singleEvents: Array<ScenarioTimeline_SingleEventFragment>;
  singleEventPortCallRelationsBySingleEventId: Map<
    number,
    SingleEventPortCallRelation
  >;
  singleEventEntries: Array<WithDbId<Required<SingleEntry>, SingleEventId>>;
  singleEventEntriesByClientId: Map<
    ClientId,
    WithDbId<SingleEntry, SingleEventId>
  >;
  shipmentByClientId: Map<ClientId, PersistedShipment>;
  scheduleData: (
    | WithDbId<Required<TCOrderEntry>, ScheduledOrderId>
    | WithDbId<Required<SingleEntry>, SingleEventId>
    | WithDbId<Required<OffHireEntry>, OffHireId>
  )[];
  allScheduleData: (
    | WithDbId<Required<TCOrderEntry>, ScheduledOrderId>
    | WithDbId<Required<SingleEntry>, SingleEventId>
    | WithDbId<Required<OffHireEntry>, OffHireId>
  )[];
  committedPortIds: LocationId[];
  portCallsByClientId: Map<
    ClientId,
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >;
  vessels: Array<Vessel>;
  vesselsById: Map<number, Vessel>;
  coas: Array<ScenarioTimeline_CoaFragment>;
  organizationsById: Map<number, ScenarioTimeline_OrganizationFragment>;
  currentUserOrganization: ScenarioTimeline_OrganizationFragment | null;
  lastCommit: Array<LastCommitFragment>;
};

const getCurrentUserOrganization = memoize2(function getCurrentUserOrganization(
  allOrgs: Array<NonNullable<ScenarioTimeline_OrganizationFragment>>,
  currentUser: Pick<User, 'organizationId'>,
): ScenarioTimeline_OrganizationFragment | null {
  const currentUserOrg = currentUser.organizationId ?? null;
  return allOrgs.find((org) => org?.id === currentUserOrg) ?? null;
});

const startOfLastWeek = moment()
  .tz('UTC')
  .add(-1, 'week')
  .startOf('week')
  .toDate();
const storedLimit =
  typeof localStorage !== 'undefined' && localStorage.getItem('completedLimit');
let defaultLimit = storedLimit ? new Date(storedLimit) : startOfLastWeek;

const noLimit = {
  completedOrderIds: new Set<number>(),
  completedSingleEventIds: new Set<number>(),
};

/*
  One potential issue, if there exists a start override which cannot be met,
  and the calculation limit removes the previous voyage which made it impossible
  to meet, it will make the start override possible to use.
  Could possibly be fixed by using start instead of startOverride for the first event
*/
export const getCompletedOrdersAndEvents = memoize4(
  function getCompletedOrdersAndEvents(
    committedOrderById: Map<number, ScenarioTimeline_OrderFragment>,
    scheduledOrderEntries: Array<
      WithDbId<Required<TCOrderEntry>, ScheduledOrderId>
    >,
    singleEvents: Array<ScenarioTimeline_SingleEventFragment>,
    { completedLimit }: { completedLimit: number },
  ): { completedOrderIds: Set<number>; completedSingleEventIds: Set<number> } {
    if (completedLimit === -Infinity) {
      return noLimit;
    }

    // Collect min and max timestamps for all orders and shipments that might be completed
    const timestampsByVessel = new DefaultMap<
      number,
      {
        time: number;
        orderId: number;
        shipmentId: number | null | undefined;
        isStart: boolean;
      }[]
    >(() => []);
    const minStartByShipment = new Map<number, number>();
    const maxEndByShipment = new Map<number, number>();

    for (const so of scheduledOrderEntries) {
      const { orderId, vesselId } = so;
      const order = committedOrderById.get(orderId);

      // TODO look into soft deleting allocations as well or some efficient way not to fetch them
      if (!vesselId || !order) continue; // No order === is soft deleted
      const timestamps = timestampsByVessel.get(vesselId);

      let min = so.loadingStart;
      let max = so.dischargingEnd;
      for (const rel of order.orderPortCallRelationsByOrderId.nodes) {
        const arrival = rel?.portCallByPortCallId?.plannedArrival;
        if (arrival) {
          min = Math.min(min, Date.parse(arrival));
        }
        const departure = rel?.portCallByPortCallId?.plannedDeparture;
        if (departure) {
          max = Math.max(max, Date.parse(departure));
        }
      }
      const { shipmentId } = order;
      if (min < completedLimit) {
        timestamps.push({ time: min, orderId, shipmentId, isStart: true });
        timestamps.push({ time: max, orderId, shipmentId, isStart: false });
      }

      if (shipmentId) {
        const currentMin = minStartByShipment.get(shipmentId) ?? Infinity;
        const currentMax = maxEndByShipment.get(shipmentId) ?? -Infinity;
        minStartByShipment.set(shipmentId, Math.min(currentMin, min));
        maxEndByShipment.set(shipmentId, Math.max(currentMax, max));
      }
    }

    // Sort timestamps
    // On end add order to candidates
    // When empty, add candidates to completed
    // Break at first event after limit
    const completedOrderIds = new Set<number>();
    for (const timestamps of timestampsByVessel.values()) {
      timestamps.sort((a, b) => a.time - b.time);
      const candidates: number[] = [];
      const carriedShipments = new Set<number>();
      let carriedOrders = 0;

      for (const { time, orderId, shipmentId, isStart } of timestamps) {
        if (time >= completedLimit) {
          break;
        } else if (isStart) {
          carriedOrders++;
          if (shipmentId && time === minStartByShipment.get(shipmentId)) {
            carriedShipments.add(shipmentId);
          }
        } else {
          candidates.push(orderId);
          carriedOrders--;
          if (shipmentId && time === maxEndByShipment.get(shipmentId)) {
            carriedShipments.delete(shipmentId);
          }
          if (carriedOrders === 0 && carriedShipments.size === 0) {
            for (const orderId of candidates) {
              completedOrderIds.add(orderId);
            }
            candidates.length = 0;
          }
        }
      }
    }

    // A single event might not be connected to a port call that has incomplete
    // order, thus we need the earliest incomplete per vessel to mark later
    // events as incomplete
    const earliestIncompleteByVessel = new DefaultMap<number, number>(
      () => completedLimit,
    );

    // If the order is not complete:
    // Add related port calls to incomplete
    // Track the earliest incomplete per vessel
    const incompletePortCallIds = new Set<number | null | undefined>();
    for (const order of committedOrderById.values()) {
      if (!completedOrderIds.has(order.id)) {
        for (const rel of order.orderPortCallRelationsByOrderId.nodes) {
          incompletePortCallIds.add(rel?.portCallId);
          const pc = rel?.portCallByPortCallId;
          const vessel = pc?.vesselId;
          if (!vessel) {
            continue;
          }
          const curr = earliestIncompleteByVessel.get(vessel);
          const arrival = pc?.plannedArrival;
          if (!arrival) {
            continue;
          }
          earliestIncompleteByVessel.set(
            vessel,
            Math.min(curr, Date.parse(arrival)),
          );
        }
      }
    }

    // If the event ends before the earliest incomplete
    // And isn't related to an incomplete port call:
    // Count as complete
    const completedSingleEventIds = new Set<number>();
    for (const se of singleEvents) {
      const vessel = se.vesselId;
      if (!vessel) {
        completedSingleEventIds.add(se.id);
        continue;
      }
      const end = Date.parse(se.end);
      const earliestIncomplete = earliestIncompleteByVessel.get(vessel);
      if (
        end < earliestIncomplete &&
        !se.singleEventPortCallRelationsBySingleEventId.nodes.some((rel) => {
          const pcId = rel?.portCallId;
          return pcId && incompletePortCallIds.has(pcId);
        })
      ) {
        completedSingleEventIds.add(se.id);
      }
    }

    return { completedOrderIds, completedSingleEventIds };
  },
);

const getCompletedPredicate = memoize(function getCompletedPredicate(
  orderIds: Set<number>,
) {
  return (so: WithDbId<Required<TCOrderEntry>, ScheduledOrderId>) =>
    orderIds.has(so.orderId);
});

const getCompletedSingleEventPredicate = memoize(
  function getCompletedSingleEventPredicate(
    completedSingleEventIds: Set<number>,
  ) {
    return (se: WithDbId<Required<SingleEntry>, SingleEventId>) =>
      completedSingleEventIds.has(se.dbId);
  },
);

const getCompletedOffHirePredicate = memoize(
  function getCompletedOffHirePredicate({
    completedLimit,
  }: {
    completedLimit: number;
  }) {
    return (e: WithDbId<Required<OffHireEntry>, OffHireId>) =>
      e.end < completedLimit;
  },
);

const getCompletedOffHireIds = memoize(function getCompletedOffHireIds(
  completedOffHireEntries: WithDbId<Required<OffHireEntry>, OffHireId>[],
) {
  return mapToSet(completedOffHireEntries, getDbId);
});

const parseVessels = memoize(function parseVessels(
  compactVessels: Array<ScenarioTimeline_ShipFragment>,
) {
  return compactVessels.map(parseVessel);
});

export function parsePlanningData(
  data: PlanningPageQuery,
  currentUser: Pick<User, 'organizationId'>,
  completedLimitObj: { completedLimit: number },
): CommittedPlanningData {
  const {
    allOrders,
    allHandledBySpots,
    allShips,
    allScheduledOrders,
    allSingleEvents,
    allOffHires,
    allCoaContracts,
    allHandledByCoas,
    allPortCalls,
    allOrganizations,
    allShipments,
  } = data;

  const orders: Array<ScenarioTimeline_OrderFragment> = compact(
    allOrders?.nodes,
  );
  const committedOrderById: Map<number, ScenarioTimeline_OrderFragment> =
    mapKeyByM(orders, getId);
  const orderPortCallRelations: Array<OrderPortCallRelation> = getOPCRs(orders);
  const orderPortCallRelationsByOrderId = mapKeyBy2M(
    orderPortCallRelations,
    getOrderId,
    getIsLoadPort,
  );
  const cargoAllocations: Map<
    ClientId,
    WithDbId<CargoAllocation, CargoCompartmentAllocationId>
  > = mapKeyByM(getCargoAllocations(orders), getCId);

  const shipments: Array<ScenarioTimeline_ShipmentFragment> = compact(
    allShipments?.nodes,
  );
  const shipmentFragments: Array<PersistedShipment> =
    getShipmentFragments(shipments);
  const shipmentByClientId = mapKeyByM<ClientId, PersistedShipment>(
    shipmentFragments,
    getCId,
  );
  const shipmentByDbId = mapKeyByM(shipmentFragments, getId);

  const portCallFragments: Array<
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  > = compact(allPortCalls?.nodes);
  const portCallFragmentsByDbId = mapKeyByM<
    number,
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  >(portCallFragments, getId);
  const portCallsByClientId: Map<
    ClientId,
    ScenarioTimeline_PortCallFragment & ScenarioTimeline_PortCall_FilesFragment
  > = mapKeyByM(portCallFragments, getCId);
  const portCallEntries: Array<WithDbId<Required<PortCallEntry>, PortCallId>> =
    getPortCallEntries(portCallFragments);
  const portCallEntriesByClientId: Map<
    ClientId,
    WithDbId<PortCallEntry, PortCallId>
  > = mapKeyByM(portCallEntries, getCId);
  const portCallEntriesByDbId: Map<
    number,
    WithDbId<PortCallEntry, PortCallId>
  > = mapKeyByM(portCallEntries, getDbId);

  const singleEvents: Array<ScenarioTimeline_SingleEventFragment> = compact(
    allSingleEvents?.nodes,
  );

  const scheduledOrders: Array<ScheduledOrderFragment> = compact(
    allScheduledOrders?.nodes,
  );
  const scheduledOrderEntries: Array<
    WithDbId<Required<TCOrderEntry>, ScheduledOrderId>
  > = getScheduledEntries(scheduledOrders, committedOrderById, shipmentByDbId);
  const { completedOrderIds, completedSingleEventIds } =
    getCompletedOrdersAndEvents(
      committedOrderById,
      scheduledOrderEntries,
      singleEvents,
      completedLimitObj,
    );
  const isScheduledOrderCompleted = getCompletedPredicate(completedOrderIds);
  const { yes: completedEntries, no: scheduledEntries } = splitM(
    scheduledOrderEntries,
    isScheduledOrderCompleted,
  );
  const tcOrderEntries: Map<
    ClientId,
    WithDbId<TCOrderEntry, ScheduledOrderId>
  > = mapKeyByM(scheduledEntries, getCId);
  const completedTcOrderEntries: Map<
    ClientId,
    WithDbId<TCOrderEntry, ScheduledOrderId>
  > = mapKeyByM(completedEntries, getCId);

  const handledByCoas: Array<ScenarioTimeline_CoaAssignmentFragment> = compact(
    allHandledByCoas?.nodes,
  );
  const handledByCoaEntries: Array<
    WithDbId<Required<CoaEntry>, HandledByCoaId>
  > = getHandledByCoaEntries(handledByCoas, committedOrderById);
  const coaEntries: Map<
    ClientId,
    WithDbId<CoaEntry, HandledByCoaId>
  > = mapKeyByM(handledByCoaEntries, getCId);

  const handledBySpots: Array<ScenarioTimeline_HandledBySpotFragment> = compact(
    allHandledBySpots?.nodes,
  );
  const handledBySpotEntries: Array<
    WithDbId<Required<SpotEntry>, HandledBySpotId>
  > = getHandledBySpotEntries(handledBySpots, committedOrderById);
  const spotEntries: Map<
    ClientId,
    WithDbId<SpotEntry, HandledBySpotId>
  > = mapKeyByM(handledBySpotEntries, getCId);

  const singleEventPortCallRelations: Array<SingleEventPortCallRelation> =
    getSingleEventPortCallRelations(singleEvents);
  const singleEventPortCallRelationsBySingleEventId = mapKeyByM(
    singleEventPortCallRelations,
    getSingleEventId,
  );
  const allSingleEventEntries: Array<
    WithDbId<Required<SingleEntry>, SingleEventId>
  > = getSingleEventEntries(singleEvents, portCallFragmentsByDbId);

  const isSingleEventCompleted = getCompletedSingleEventPredicate(
    completedSingleEventIds,
  );
  const { yes: completedSingleEventEntries, no: singleEventEntries } = splitM(
    allSingleEventEntries,
    isSingleEventCompleted,
  );
  const singleEventEntriesByClientId: Map<
    ClientId,
    WithDbId<SingleEntry, SingleEventId>
  > = mapKeyByM(singleEventEntries, getCId);

  const offHires: Array<ScenarioTimeline_OffHireFragment> = compact(
    allOffHires?.nodes,
  );
  const allOffHireEntries: Array<WithDbId<Required<OffHireEntry>, OffHireId>> =
    getOffHireEntries(offHires);
  const isOffHireCompleted = getCompletedOffHirePredicate(completedLimitObj);
  const { yes: completedOffHireEntries, no: offHireEntries } = splitM(
    allOffHireEntries,
    isOffHireCompleted,
  );
  const completedOffHireIds = getCompletedOffHireIds(completedOffHireEntries);
  const offHireEntriesByClientId: Map<
    ClientId,
    WithDbId<OffHireEntry, OffHireId>
  > = mapKeyByM(offHireEntries, getCId);

  const committedPortIds = getLocationIds(orders, singleEvents, offHires);

  const scheduleData = getScheduleData(
    scheduledEntries,
    singleEventEntries,
    offHireEntries,
  );

  const allScheduleData = getScheduleData(
    scheduledOrderEntries,
    allSingleEventEntries,
    allOffHireEntries,
  );

  const compactVessels = compact(allShips?.nodes);
  const vessels = parseVessels(compactVessels);
  const vesselsById = mapKeyByM(vessels, getId);

  const coas: Array<ScenarioTimeline_CoaFragment> = compact(
    allCoaContracts?.nodes,
  );

  const allOrgs = compact(allOrganizations?.nodes);
  const organizationsById = mapKeyByM(allOrgs, getId);
  const currentUserOrganization = getCurrentUserOrganization(
    allOrgs,
    currentUser,
  );

  const lastCommit = compact(data.lastCommit?.nodes);
  return {
    orders,
    completedOrderIds,
    completedOffHireIds,
    completedSingleEventIds,
    cargoAllocations,
    handledByCoas,
    coaEntries,
    handledBySpots,
    spotEntries,
    scheduledEntries,
    tcOrderEntries,
    completedEntries: getScheduleData(
      completedEntries,
      completedSingleEventEntries,
      emptyArr,
    ),
    completedTcOrderEntries,
    offHires,
    offHireEntries,
    offHireEntriesByClientId,
    portCallFragments,
    singleEvents,
    singleEventEntries,
    singleEventEntriesByClientId,
    portCallEntriesByClientId,
    portCallEntriesByDbId,
    singleEventPortCallRelationsBySingleEventId,
    orderPortCallRelationsByOrderId,
    committedOrderById,
    portCallFragmentsByDbId,
    shipmentByClientId,
    scheduleData,
    allScheduleData,
    committedPortIds,
    portCallsByClientId,
    vessels,
    vesselsById,
    coas,
    organizationsById,
    currentUserOrganization,
    lastCommit,
  };
}
